// services/syncService.js
import axios from '@/api/axios';
import dataService from '@/services/dataService';
import logService from '@/services/logService';
import localforage from 'localforage';

// Le code a été amélioré pour utiliser les méthodes spécifiques de dataService
// au lieu d'accéder directement aux stores

class SyncService {
  constructor() {
    this.syncInProgress = false;
    this.syncQueue = [];
    this.lastSyncTime = null;
    this.syncRetryTimeouts = {};
    this.indexedDBRetryTimeouts = {}; // Pour gérer les timeouts de reconnexion à IndexedDB
    this.indexedDBErrorCount = 0; // Compteur d'erreurs IndexedDB
    this.indexedDBReconnecting = false; // Drapeau pour éviter de multiples tentatives de reconnexion
    this.MAX_INDEXEDDB_ERRORS = 5; // Nombre maximum d'erreurs avant de passer en mode dégradé
    this.useIndexedDBFallback = false; // Indicateur pour savoir si on utilise le fallback (localStorage)
    
    // Initialiser le store pour les objets images
    if (typeof localforage !== 'undefined') {
      this.imageObjectsStore = localforage.createInstance({
        name: 'images',
        storeName: 'image_objects'
      });
    }
    
    // Écouter les événements de visibilité pour réinitialiser les connexions IndexedDB quand nécessaire
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
    }

    // Définir une expression régulière pour les shop_deliveries
    this.shopDeliveryRegex = /shop_delivery$/i;
  }
  
  // Méthode pour gérer le changement de visibilité de la page
  handleVisibilityChange() {
    if (document.visibilityState === 'visible') {
      // Quand la page redevient visible, vérifier la connexion IndexedDB
      this.checkAndReconnectIndexedDB();
    }
  }
  
  // Méthode pour gérer une erreur d'IndexedDB
  handleIndexedDBError(operation, error) {
    this.indexedDBErrorCount++;
    
    logService.syncError(`Erreur IndexedDB lors de l'opération: ${operation}`, {
      error: error.message,
      errorName: error.name,
      errorCount: this.indexedDBErrorCount,
      stack: error.stack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      platform: navigator.platform
    });
    
    // Si c'est une erreur de connexion perdue, tenter une reconnexion
    if (error.message && (
        error.message.includes('Connection to Indexed Database server lost') ||
        error.message.includes('A mutation operation was attempted on a database that did not allow mutations') ||
        error.message.includes('The operation failed for reasons unrelated to the database itself') ||
        error.message.includes('UnknownError') ||
        error.message.includes('QuotaExceededError')
       )) {
      this.checkAndReconnectIndexedDB();
    }
    
    // Si trop d'erreurs, passer en mode dégradé (localStorage)
    if (this.indexedDBErrorCount >= this.MAX_INDEXEDDB_ERRORS && !this.useIndexedDBFallback) {
      this.useIndexedDBFallback = true;
      logService.syncWarning(`Passage en mode dégradé (localStorage) après ${this.indexedDBErrorCount} erreurs IndexedDB`, {
        lastError: error.message,
        userAgent: navigator.userAgent
      });
      
      // Notifier l'utilisateur
      if (typeof window !== 'undefined') {
        // Créer un événement pour indiquer le problème de stockage
        const event = new CustomEvent('storage-degraded', {
          detail: {
            reason: "IndexedDB disconnected",
            message: "Problème de stockage détecté. Certaines fonctionnalités peuvent être limitées.",
            timestamp: Date.now()
          }
        });
        
        window.dispatchEvent(event);
      }
    }
  }
  
  // Méthode pour vérifier et potentiellement reconnecter à IndexedDB
  async checkAndReconnectIndexedDB() {
    // Éviter les tentatives multiples simultanées
    if (this.indexedDBReconnecting) return;
    
    this.indexedDBReconnecting = true;
    
    try {
      logService.syncInfo("Tentative de reconnexion à IndexedDB...");
      
      // Demander à dataService de réinitialiser ses connexions
      if (typeof dataService.resetIndexedDBConnection === 'function') {
        await dataService.resetIndexedDBConnection();
        
        // Test de la connexion en essayant une opération simple
        await dataService.testConnection();
        
        // Si on arrive ici, la connexion est restaurée
        this.indexedDBErrorCount = 0;
        this.useIndexedDBFallback = false;
        
        logService.syncInfo("Reconnexion à IndexedDB réussie");
        
        // Déclencher un événement pour indiquer que le stockage est à nouveau normal
        if (typeof window !== 'undefined') {
          const event = new CustomEvent('storage-restored', {
            detail: {
              timestamp: Date.now()
            }
          });
          
          window.dispatchEvent(event);
        }
      }
    } catch (error) {
      logService.syncError("Échec de la reconnexion à IndexedDB", {
        error: error.message,
        stack: error.stack
      });
      
      // Planifier une nouvelle tentative après un délai
      setTimeout(() => {
        this.indexedDBReconnecting = false;
        this.checkAndReconnectIndexedDB();
      }, 10000); // 10 secondes
    } finally {
      this.indexedDBReconnecting = false;
    }
  }
  
  // Méthode pour tenter une opération avec gestion robuste des erreurs IndexedDB
  async tryWithIndexedDBFallback(operation, fallbackOperation) {
    try {
      // Si nous sommes en mode dégradé, utiliser directement le fallback
      if (this.useIndexedDBFallback && fallbackOperation) {
        return await fallbackOperation();
      }
      
      // Sinon, essayer l'opération normale
      return await operation();
    } catch (error) {
      // Gérer spécifiquement les erreurs IndexedDB
      if (error.name === 'UnknownError' || 
          error.message.includes('IndexedDB') || 
          error.message.includes('Connection to Indexed Database server lost')) {
        
        this.handleIndexedDBError(operation.name || 'unknown', error);
        
        // Si un fallback est fourni, l'utiliser
        if (fallbackOperation) {
          try {
            return await fallbackOperation();
          } catch (fallbackError) {
            // Si même le fallback échoue, lever l'erreur originale
            throw error;
          }
        }
      }
      
      // Pour les autres types d'erreurs, les propager normalement
      throw error;
    }
  }

  // Méthode pour synchroniser une image
  async syncImage(imageData) {
    try {
      // Si l'image a déjà été synchronisée, ne rien faire
      if (imageData.synced && imageData.serverImageId) {
        return imageData.serverImageId;
      }
      
      // Récupérer l'email pour les logs
      const userEmail = localStorage.getItem('email') || 'email_non_disponible';
      
      // Si l'appareil n'est pas en ligne, impossible de synchroniser
      if (!navigator.onLine) {
        logService.syncWarning(`Tentative de synchronisation de l'image ${imageData.id} impossible car hors ligne`, {
          deliveryId: imageData.deliveryId,
          userEmail,
          timestamp: new Date().toISOString()
        });
        return null;
      }
      
      // Déterminer le type d'image (shop_delivery ou standard)
      const isShopDelivery = 
          imageData.type === 'shop_delivery' || 
          imageData.deliveryContext?.includes('shop') || 
          imageData.context?.includes('shop') ||
          (imageData.id && (
            imageData.id.startsWith('shop_img_') || 
            imageData.id.includes('_shop_')
          ));
      
      if (isShopDelivery) {
        // Si c'est une image shop_delivery, rediriger vers la méthode spécifique
        console.log(`Image ${imageData.id} détectée comme shop_delivery, redirection vers syncShopDeliveryImage`);
        return this.syncShopDeliveryImage(imageData);
      }
      
      // Essayer de récupérer les informations de la livraison pour enrichir les logs
      let deliveryInfo = null;
      try {
        const delivery = await dataService.getDelivery(imageData.deliveryId);
        if (delivery) {
          deliveryInfo = {
            id: delivery.id,
            address: delivery.address,
            client: delivery.client,
            status: delivery.status,
            tourneeId: delivery.tournee_id,
            reference: delivery.reference
          };
        }
      } catch (deliveryError) {
        console.warn(`Impossible de récupérer les infos de la livraison ${imageData.deliveryId}: ${deliveryError.message}`);
      }
      
      // Extraire l'image du blob stocké
      let imageFile = imageData.file;
      
      // Vérifier si le fichier est un ArrayBuffer ou une instance de Blob/File
      if (imageFile && !(imageFile instanceof Blob || imageFile instanceof File)) {
        // Si c'est un ArrayBuffer ou un autre format non-File, le convertir en File
        if (imageFile instanceof ArrayBuffer || ArrayBuffer.isView(imageFile)) {
          const blob = new Blob([imageFile], { type: imageData.fileType || 'image/jpeg' });
          imageFile = new File([blob], imageData.fileName || `image_${Date.now()}.jpg`, {
            type: imageData.fileType || 'image/jpeg',
            lastModified: Date.now()
          });
          
          logService.syncInfo(`Conversion d'ArrayBuffer en File pour l'image ${imageData.id}`, {
            deliveryId: imageData.deliveryId,
            fileName: imageFile.name,
            fileSize: imageFile.size
          });
        } else if (typeof imageFile === 'string' && imageFile.startsWith('data:')) {
          // Si c'est une data URL, la convertir en Blob puis en File
          try {
            const response = await fetch(imageFile);
            const blob = await response.blob();
            imageFile = new File([blob], imageData.fileName || `image_${Date.now()}.jpg`, {
              type: imageData.fileType || blob.type || 'image/jpeg',
              lastModified: Date.now()
            });
            
            logService.syncInfo(`Conversion de data URL en File pour l'image ${imageData.id}`, {
              deliveryId: imageData.deliveryId,
              fileName: imageFile.name,
              fileSize: imageFile.size
            });
          } catch (conversionError) {
            logService.syncError(`Échec de conversion data URL en File pour l'image ${imageData.id}`, {
              error: conversionError.message,
              deliveryId: imageData.deliveryId
            });
            throw new Error(`Impossible de convertir l'image: ${conversionError.message}`);
          }
        } else {
          logService.syncError(`Format de fichier non reconnu pour l'image ${imageData.id}`, {
            deliveryId: imageData.deliveryId,
            fileType: typeof imageFile
          });
          throw new Error('Format de fichier non reconnu');
        }
      }
      
      // Si après toutes ces vérifications on n'a toujours pas de fichier valide, abandonner
      if (!imageFile || !(imageFile instanceof Blob || imageFile instanceof File)) {
        logService.syncError(`Données d'image invalides pour ${imageData.id}`, {
          deliveryId: imageData.deliveryId,
          fileType: typeof imageFile
        });
        throw new Error('Données d\'image invalides');
      }
      
      const imageMetadata = {
        id: imageData.id,
        fileName: imageData.fileName || (imageFile.name || `image_${Date.now()}.jpg`),
        fileType: imageData.fileType || imageFile.type || 'image/jpeg',
        size: imageData.size || (imageFile ? imageFile.size : 0),
        deliveryId: imageData.deliveryId,
        deliveryContext: imageData.deliveryContext,
        timestamp: imageData.timestamp,
        syncAttempts: imageData.syncAttempts || 0
      };

      // Vérifier si l'image doit être compressée davantage en fonction du nombre de tentatives
      // Plus il y a eu d'échecs, plus on compresse l'image
      const shouldCompressMore = imageData.syncAttempts > 0 && imageFile && imageFile.size > 500000; // > 500KB
      
      if (shouldCompressMore) {
        try {
          const compressionQuality = Math.max(0.5, 0.9 - (imageData.syncAttempts * 0.2)); // Diminue la qualité à chaque tentative
          const maxSize = Math.min(1500, 2400 - (imageData.syncAttempts * 300)); // Diminue la taille max à chaque tentative
          
          logService.syncInfo(`Compression supplémentaire de l'image ${imageData.id} (tentative #${imageData.syncAttempts}) - Qualité: ${compressionQuality.toFixed(2)}, MaxSize: ${maxSize}px`, {
            deliveryId: imageData.deliveryId,
            originalSize: imageFile.size
          });
          
          imageFile = await this.compressImage(imageFile, compressionQuality, maxSize);
          
          logService.syncInfo(`Image ${imageData.id} recompressée avec succès`, {
            deliveryId: imageData.deliveryId,
            originalSize: imageMetadata.size,
            newSize: imageFile.size,
            compressionRatio: (imageFile.size / imageMetadata.size).toFixed(2)
          });
        } catch (compressionError) {
          // En cas d'échec de compression, continuer avec l'image originale
          logService.syncWarning(`Échec de la compression supplémentaire pour l'image ${imageData.id}`, {
            error: compressionError.message,
            deliveryId: imageData.deliveryId
          });
        }
      }
      
      logService.syncInfo(`Envoi de l'image ${imageData.id} au serveur`, {
        deliveryId: imageData.deliveryId,
        fileSize: imageFile ? imageFile.size : 'inconnu',
        userEmail,
        deliveryContext: imageData.deliveryContext,
        deliveryInfo,
        timestamp: new Date().toISOString()
      });
      
      // Créer le FormData pour l'upload
      const formData = new FormData();
      formData.append('image', imageFile);
      formData.append('delivery_id', imageData.deliveryId);
      formData.append('deliveryContext', imageData.deliveryContext);
      
      // Log what's being sent to help debug
      logService.syncInfo(`Données FormData envoyées pour l'image ${imageData.id}`, {
        deliveryId: imageData.deliveryId,
        deliveryContext: imageData.deliveryContext,
        formDataKeys: [...formData.keys()],
        imageFileType: imageFile.type,
        imageFileName: imageFile.name,
        imageFileSize: imageFile.size
      });
      
      // Ajouter des métadonnées supplémentaires
      const metadata = {
        timestamp: imageData.timestamp,
        uploadedAt: new Date().toISOString(),
        userEmail,
        deviceInfo: {
          userAgent: navigator.userAgent,
          platform: navigator.platform
        }
      };
      formData.append('metadata', JSON.stringify(metadata));
      
      // Calculer un timeout adapté en fonction de la taille de l'image et des tentatives précédentes
      // Plus l'image est grande ou plus il y a eu de tentatives, plus le timeout est long
      const baseTimeout = 30000; // 30s de base
      const sizeFactor = imageFile ? Math.min(3, imageFile.size / 1000000) : 1; // Facteur basé sur la taille (MB), max 3x
      const attemptFactor = 1 + (imageData.syncAttempts || 0) * 0.5; // Facteur basé sur le nombre de tentatives
      const timeout = Math.round(baseTimeout * sizeFactor * attemptFactor); // Timeout calculé
      
      // Configurer la requête avec un timeout adapté
      const config = {
        timeout: timeout, // Timeout dynamique
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      };
      
      // Envoyer l'image au serveur
      const response = await axios.post('/livraison/deliveryImage/', formData, config);
      
      // Récupérer l'ID de l'image côté serveur
      const serverId = response.data.id;
      
      // Marquer l'image comme synchronisée dans le stockage local
      imageData.synced = true;
      imageData.serverImageId = serverId;
      imageData.syncedAt = new Date().toISOString();
      
      // Mettre à jour les données de l'image dans le stockage
      try {
        await dataService.updatePendingImage(imageData.id, imageData);
      } catch (storageError) {
        logService.syncWarning(`Impossible de mettre à jour le statut de l'image ${imageData.id} après synchronisation`, {
          error: storageError.message,
          deliveryId: imageData.deliveryId
        });
      }
      
      logService.syncInfo(`Image ${imageData.id} synchronisée avec succès (server ID: ${serverId})`, {
        deliveryId: imageData.deliveryId,
        userEmail,
        serverId,
        timestamp: new Date().toISOString()
      });
      
      return serverId;
    } catch (error) {
      // Les erreurs réseau et autres erreurs (hors IndexedDB) sont gérées ici
      
      // Vérifier si c'est une erreur IndexedDB et la gérer spécifiquement
      if (error.name === 'UnknownError' || 
          error.message.includes('IndexedDB') || 
          error.message.includes('Connection to Indexed Database server lost')) {
        this.handleIndexedDBError('syncImage', error);
      }
      
      // Récupérer l'email même en cas d'erreur
      const userEmail = localStorage.getItem('email') || 'email_non_disponible';
      
      // Récupérer des détails sur l'erreur réseau
      const networkDetails = {
        online: navigator.onLine,
        connection: navigator.connection ? {
          effectiveType: navigator.connection.effectiveType,
          downlink: navigator.connection.downlink,
          rtt: navigator.connection.rtt,
          saveData: navigator.connection.saveData
        } : null
      };
      
      // Analyser l'erreur pour obtenir plus de détails
      const errorDetails = {
        name: error.name,
        message: error.message,
        code: error.code,
        stack: error.stack,
        isAxiosError: error.isAxiosError || false,
        request: error.request ? {
          url: error.config?.url,
          method: error.config?.method,
          headers: error.config?.headers,
          timeout: error.config?.timeout
        } : null,
        response: error.response ? {
          status: error.response.status,
          statusText: error.response.statusText,
          headers: error.response.headers,
          data: error.response.data
        } : null
      };
      
      // Essayer de récupérer les informations de la livraison pour enrichir les logs
      let deliveryInfo = null;
      try {
        if (imageData && imageData.deliveryId) {
          const delivery = await this.tryWithIndexedDBFallback(
            async () => await dataService.getDelivery(imageData.deliveryId),
            async () => {
              // Fallback: essayer de récupérer depuis localStorage si présent
              const localStorageKey = `delivery_${imageData.deliveryId}`;
              const cachedData = localStorage.getItem(localStorageKey);
              return cachedData ? JSON.parse(cachedData) : null;
            }
          );
          
          if (delivery) {
            deliveryInfo = {
              id: delivery.id,
              address: delivery.address,
              client: delivery.client,
              status: delivery.status,
              tourneeId: delivery.tournee_id,
              reference: delivery.reference
            };
          }
        }
      } catch (secondaryError) {
        // Ignorer les erreurs supplémentaires ici
      }
      
      // Extraire les métadonnées de l'image pour les logs
      const imageMetadata = {
        id: imageData?.id,
        fileName: imageData?.fileName,
        fileType: imageData?.fileType,
        size: imageData?.size || (imageData?.file ? imageData.file.size : 0),
        deliveryId: imageData?.deliveryId,
        deliveryContext: imageData?.deliveryContext,
        timestamp: imageData?.timestamp,
        syncAttempts: imageData?.syncAttempts || 0
      };
      
      logService.syncError(`Erreur lors de la synchronisation de l'image ${imageData?.id}`, {
        deliveryId: imageData?.deliveryId,
        error: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        responseData: error.response?.data,
        userEmail,
        imageMetadata,
        networkDetails,
        errorDetails,
        deliveryInfo,
        deviceInfo: {
          userAgent: navigator.userAgent,
          platform: navigator.platform,
          language: navigator.language,
          memory: navigator.deviceMemory,
          vendor: navigator.vendor,
          appCodeName: navigator.appCodeName
        },
        timestamp: new Date().toISOString()
      });
      
      // Marquer l'image avec une tentative échouée pour réessayer plus tard
      try {
        const updatedImageData = { ...imageData };
        updatedImageData.syncAttempts = (imageData.syncAttempts || 0) + 1;
        updatedImageData.lastSyncAttempt = new Date().toISOString();
        updatedImageData.lastError = {
          message: error.message,
          status: error.response?.status,
          statusText: error.response?.statusText,
          time: new Date().toISOString(),
          type: error.name || 'UnknownError',
          isNetworkError: !navigator.onLine || error.message.includes('Network Error')
        };
        
        // Si l'erreur indique un problème avec le format de l'image, le marquer
        if (error.response?.data?.detail?.includes('image') || 
            error.message.includes('image') ||
            error.response?.status === 415) {
          updatedImageData.hasFormatError = true;
          updatedImageData.formatErrorDetails = error.response?.data?.detail || error.message;
        }
        
        // Planifier une nouvelle tentative avec backoff exponentiel
        this.scheduleImageRetry(imageData.id, updatedImageData);
        
        // Mettre à jour les données de l'image dans le stockage
        await this.tryWithIndexedDBFallback(
          async () => await dataService.updatePendingImage(updatedImageData.id, updatedImageData),
          async () => {
            // Fallback: mise à jour dans localStorage
            const pendingImagesKey = `pendingImages_${updatedImageData.deliveryId}`;
            let pendingImages = [];
            const storedData = localStorage.getItem(pendingImagesKey);
            
            if (storedData) {
              pendingImages = JSON.parse(storedData);
              const index = pendingImages.findIndex(img => img.id === updatedImageData.id);
              if (index !== -1) {
                pendingImages[index] = updatedImageData;
              } else {
                pendingImages.push(updatedImageData);
              }
            } else {
              pendingImages = [updatedImageData];
            }
            
            localStorage.setItem(pendingImagesKey, JSON.stringify(pendingImages));
            return true;
          }
        );
      } catch (storageError) {
        logService.syncError(`Impossible de mettre à jour les informations de tentative pour l'image ${imageData?.id}`, {
          error: storageError.message,
          deliveryId: imageData?.deliveryId,
          originalError: error.message
        });
      }
      
      return null;
    }
  }

  // Méthode pour compresser une image avec qualité et taille maximale ajustables
  compressImage(file, quality = 0.7, maxDimension = 1500) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);

      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;

        img.onload = () => {
          // Créer un canvas pour la compression
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          // Redimensionner proportionnellement si nécessaire
          if (width > height) {
            if (width > maxDimension) {
              height *= maxDimension / width;
              width = maxDimension;
            }
          } else {
            if (height > maxDimension) {
              width *= maxDimension / height;
              height = maxDimension;
            }
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          // Compresser en JPEG avec la qualité spécifiée
          canvas.toBlob((blob) => {
            if (blob) {
              // Créer un nouveau File à partir du Blob
              const compressedFile = new File([blob], file.name || `compressed_${Date.now()}.jpg`, {
                type: 'image/jpeg',
                lastModified: Date.now()
              });
              resolve(compressedFile);
            } else {
              reject(new Error("Échec de la compression de l'image"));
            }
          }, 'image/jpeg', quality);
        };

        img.onerror = () => {
          reject(new Error("Impossible de charger l'image pour la compression"));
        };
      };

      reader.onerror = () => {
        reject(new Error("Impossible de lire le fichier image"));
      };
    });
  }

  // Méthode pour planifier une réessai de synchronisation d'image
  scheduleImageRetry(imageId, imageData) {
    // Nettoyer tout timeout existant pour cette image
    if (this.syncRetryTimeouts[imageId]) {
      clearTimeout(this.syncRetryTimeouts[imageId]);
    }
    
    // Récupérer les informations de connexion réseau
    const networkInfo = {
      online: navigator.onLine,
      connection: navigator.connection ? {
        effectiveType: navigator.connection.effectiveType,
        downlink: navigator.connection.downlink,
        rtt: navigator.connection.rtt
      } : null
    };
    
    // Extraire le type de connexion et le débit
    const connectionType = networkInfo.connection?.effectiveType || '4g';
    const downlink = networkInfo.connection?.downlink || 1.0;
    
    // Calculer le facteur de délai basé sur la qualité de la connexion
    let connectionFactor = 1.0;
    if (connectionType === '2g' || downlink < 0.1) {
      connectionFactor = 4.0; // Connexion très lente
    } else if (connectionType === '3g' || downlink < 0.5) {
      connectionFactor = 2.0; // Connexion lente
    } else if (downlink < 1.0) {
      connectionFactor = 1.5; // Connexion moyenne
    }
    
    // Facteur basé sur la taille de l'image
    const imageSize = imageData.size || 0;
    let sizeFactor = 1.0;
    if (imageSize > 1500000) { // > 1.5 MB
      sizeFactor = 3.0;
    } else if (imageSize > 800000) { // > 800 KB
      sizeFactor = 2.0;
    } else if (imageSize > 300000) { // > 300 KB
      sizeFactor = 1.5;
    }
    
    // Calculer le délai de backoff avec les facteurs
    const attempts = imageData.syncAttempts || 0;
    const baseDelay = 5000; // 5 secondes
    const maxDelay = 5 * 60 * 1000; // 5 minutes max
    
    // Formule de backoff exponentiel avec plafond et facteurs
    const calculatedDelay = baseDelay * Math.pow(2, attempts) * connectionFactor * sizeFactor;
    const delay = Math.min(maxDelay, calculatedDelay);
    
    // Planifier la nouvelle tentative
    this.syncRetryTimeouts[imageId] = setTimeout(async () => {
      // Vérifier si l'appareil est en ligne
      if (navigator.onLine) {
        try {
          // Récupérer les données fraîches depuis le stockage
          const freshImageData = await dataService.getPendingImage(imageId);
          if (freshImageData && !freshImageData.synced) {
            logService.syncInfo(`Tentative automatique de synchronisation de l'image ${imageId}`, {
              deliveryId: freshImageData.deliveryId,
              attempts: freshImageData.syncAttempts,
              lastError: freshImageData.lastError,
              timestamp: new Date().toISOString()
            });
            
            // Tenter de synchroniser l'image
            await this.syncImage(freshImageData);
          } else {
            // L'image n'existe plus ou est déjà synchronisée
            logService.syncInfo(`Image ${imageId} déjà synchronisée ou supprimée, annulation de la nouvelle tentative`);
          }
        } catch (error) {
          // Stocker les attributs initiaux que nous connaissons déjà pour les logs
          // au cas où freshImageData n'est pas disponible
          const safeLogData = {
            error: error.message,
            imageId: imageId,
            // Utiliser les données de l'image originale comme fallback si freshImageData n'est pas disponible
            deliveryId: imageData?.deliveryId,
            attempts: imageData?.syncAttempts,
            networkStatus: {
              online: navigator.onLine,
              connection: navigator.connection ? {
                effectiveType: navigator.connection.effectiveType,
                downlink: navigator.connection.downlink
              } : null
            }
          };
          
          try {
            // Essayer de récupérer les informations de la livraison pour enrichir les logs d'erreur
            if (imageData && imageData.deliveryId) {
              try {
                const delivery = await this.tryWithIndexedDBFallback(
                  async () => await dataService.getDelivery(imageData.deliveryId),
                  async () => {
                    // Fallback: essayer de récupérer depuis localStorage si présent
                    const localStorageKey = `delivery_${imageData.deliveryId}`;
                    const cachedData = localStorage.getItem(localStorageKey);
                    return cachedData ? JSON.parse(cachedData) : null;
                  }
                );
                
                if (delivery) {
                  safeLogData.deliveryInfo = {
                    id: delivery.id,
                    address: delivery.address,
                    client: delivery.client,
                    status: delivery.status,
                    tourneeId: delivery.tournee_id,
                    reference: delivery.reference
                  };
                }
              } catch (deliveryError) {
                // Continuer sans les infos de livraison
              }
            }
          } catch (secondaryError) {
            // Ne pas laisser une erreur secondaire interrompre le flux
          }
          
          // Log sécurisé sans référence à freshImageData
          logService.syncError(`Échec de la nouvelle tentative de synchronisation pour l'image ${imageId}`, safeLogData);
          
          // Vérifier si nous avons atteint le nombre maximal de tentatives (10)
          if (imageData && imageData.syncAttempts >= 10) {
            logService.syncWarning(`Abandon des tentatives automatiques de synchronisation pour l'image ${imageId} après ${imageData.syncAttempts} échecs`, {
              deliveryId: imageData.deliveryId,
              imageId: imageId,
              requiresManualSync: true
            });
            
            try {
              // Marquer l'image pour synchronisation manuelle
              const updatedImage = { ...imageData, requiresManualSync: true };
              await dataService.updatePendingImage(imageId, updatedImage);
            } catch (updateError) {
              // Ignorer les erreurs de mise à jour
            }
            
            // Ne pas replanifier de nouvelle tentative
            return;
          }
        }
      } else {
        // Si hors ligne, planifier une autre tentative ultérieurement
        logService.syncInfo(`Appareil hors ligne, report de la tentative pour l'image ${imageId}`);
        
        // Replanifier avec un délai fixe
        const offlineRetryDelay = 2 * 60 * 1000; // 2 minutes
        setTimeout(() => {
          delete this.syncRetryTimeouts[imageId]; // Supprimer d'abord
          // Utiliser les données d'origine pour replanifier
          this.scheduleImageRetry(imageId, imageData);
        }, offlineRetryDelay);
      }
      
      // Supprimer cette entrée du tableau de timeouts
      delete this.syncRetryTimeouts[imageId];
    }, delay);
    
    // Formater la durée de façon lisible (seconds ou minutes)
    const delayFormatted = delay < 60000 
      ? `${Math.round(delay/1000)}s` 
      : `${Math.round(delay/60000)}min ${Math.round((delay%60000)/1000)}s`;
    
    // Log détaillé pour le backoff
    logService.syncInfo(`Planification de nouvelle tentative pour l'image ${imageId} dans ${delayFormatted}`, {
      deliveryId: imageData.deliveryId,
      attempts: imageData.syncAttempts,
      lastError: imageData.lastError,
      imageSize: imageData.size,
      connectionType,
      downlink,
      connectionFactor,
      sizeFactor,
      calculatedDelay: Math.round(calculatedDelay/1000) + 's',
      actualDelay: delayFormatted
    });
  }

  // Méthode pour synchroniser une mise à jour
  async syncUpdate(updateData) {
    try {
      // Vérifier que les données sont valides
      if (!updateData || !updateData.deliveryId) {
        throw new Error("Données de mise à jour invalides");
      }

      // Récupérer l'email de l'utilisateur depuis le localStorage
      const userEmail = localStorage.getItem('email') || 'email_non_disponible';

      // Arrays pour stocker les IDs d'images
      const serverImageIds = [];
      const processedLocalImageIds = [];
      const failedImageIds = [];
      const imageErrorDetails = []; // Stocke les détails d'erreur pour chaque image échouée

      // Essayer de récupérer les informations de la livraison pour enrichir les logs
      let deliveryInfo = null;
      try {
        const delivery = await dataService.getDelivery(updateData.deliveryId);
        if (delivery) {
          deliveryInfo = {
            id: delivery.id,
            address: delivery.address,
            client: delivery.client,
            status: delivery.status,
            tourneeId: delivery.tournee_id,
            reference: delivery.reference,
            currentServiceStatus: delivery.current_service_status,
            plannedTime: delivery.planned_time,
            deliveryNote: delivery.delivery_note,
            lastUpdate: delivery.last_update
          };
        }
      } catch (deliveryFetchError) {
        logService.syncWarning(`Impossible de récupérer les infos de livraison pour la mise à jour ${updateData.id}`, {
          deliveryId: updateData.deliveryId,
          error: deliveryFetchError.message,
          userEmail
        });
      }

      // Synchroniser les images d'abord, MAIS de façon indépendante
      // afin que l'échec des images n'empêche pas la synchronisation du statut de livraison
      if (updateData.imageIds?.length) {
        logService.syncInfo(`Synchronisation de ${updateData.imageIds.length} images pour la mise à jour ${updateData.id}`, {
          deliveryId: updateData.deliveryId,
          userEmail,
          imageIds: updateData.imageIds,
          deliveryInfo: deliveryInfo,
          updateContext: updateData.context || 'unknown_context',
          updateStatus: updateData.status
        });
        
        // Synchroniser les images une par une
        for (const imageId of updateData.imageIds) {
          // Vérifier si cette image a déjà été traitée
          if (processedLocalImageIds.includes(imageId)) continue;
          
          processedLocalImageIds.push(imageId);
          
          // Récupérer les données de l'image
          const imageData = await dataService.getPendingImage(imageId);
          
          if (!imageData) {
            logService.syncWarning(`Image ${imageId} non trouvée dans le stockage local pour la mise à jour ${updateData.id}`, {
              deliveryId: updateData.deliveryId,
              userEmail,
              deliveryInfo: deliveryInfo
            });
            continue;
          }

          // Vérifier si l'image a déjà un ID serveur (pré-uploadée par ProgressiveImageUploader)
          if (imageData.serverImageId) {
            serverImageIds.push(imageData.serverImageId);
            logService.syncInfo(`Image ${imageId} déjà uploadée avec ID serveur ${imageData.serverImageId}`, {
              deliveryId: updateData.deliveryId,
              userEmail,
              fileName: imageData.fileName,
              fileType: imageData.fileType,
              deliveryInfo: deliveryInfo
            });
            continue;
          }
          
          // Si l'image a déjà été synchronisée mais est encore dans la base
          if (imageData.synced && imageData.serverImageId) {
            serverImageIds.push(imageData.serverImageId);
            continue;
          }
          
          // Si l'image n'est pas encore synchronisée, essayer de la synchroniser
          if (!imageData.synced) {
            try {
              const serverId = await this.syncImage(imageData);
              if (serverId) {
                serverImageIds.push(serverId);
              }
            } catch (imageError) {
              failedImageIds.push(imageId);
              
              // Collecter les détails de l'erreur pour le rapport final
              const errorDetail = {
                imageId,
                fileName: imageData.fileName,
                fileType: imageData.fileType,
                fileSize: imageData.size,
                errorMessage: imageError.message,
                errorCode: imageError.response?.status,
                errorType: imageError.name || 'UnknownError',
                isNetworkError: !navigator.onLine || imageError.message.includes('Network Error'),
                context: imageData.deliveryContext,
                timestamp: new Date().toISOString()
              };
              
              imageErrorDetails.push(errorDetail);
              
              logService.syncError(`Impossible de synchroniser l'image ${imageId} pour la mise à jour ${updateData.id}, mais on continue`, {
                deliveryId: updateData.deliveryId,
                error: imageError.message,
                userEmail,
                status: imageError.response?.status,
                responseData: imageError.response?.data,
                fileName: imageData.fileName,
                fileType: imageData.fileType,
                fileSize: imageData.size,
                imageContext: imageData.deliveryContext,
                deliveryInfo: deliveryInfo
              });
              // Continuer avec les autres images et la mise à jour même si celle-ci échoue
            }
          }
        }

        // Attendre un court délai pour s'assurer que le serveur a traité les images
        // qui ont réussi avant de soumettre la mise à jour de la livraison
        if (serverImageIds.length > 0) {
          await new Promise(resolve => setTimeout(resolve, 1000)); // 1 seconde d'attente
        } else if (failedImageIds.length > 0) {
          // Toutes les images ont échoué, mais on continue avec la mise à jour du statut
          logService.syncWarning(`Aucune image n'a pu être synchronisée pour la livraison ${updateData.deliveryId}, mais on continue avec la mise à jour du statut`, {
            deliveryId: updateData.deliveryId,
            failedImages: failedImageIds.length,
            imageErrors: imageErrorDetails,
            deliveryInfo: deliveryInfo,
            updateStatus: updateData.status
          });
        }
      }

      // TOUJOURS tenter de mettre à jour le statut de la livraison, même si les images ont échoué
      if (navigator.onLine) {
        const updatePayload = {
          status: updateData.status,
          undone_reason: updateData.undoneReason,
          image_ids: serverImageIds // Toujours utiliser les IDs serveur des images qui ont réussi
        };

        // Si des IDs serveur sont déjà fournis (préalablement uploadés), les ajouter
        if (updateData.serverImageIds && updateData.serverImageIds.length > 0) {
          updatePayload.image_ids = [
            ...updatePayload.image_ids,
            ...updateData.serverImageIds
          ];
          
          logService.syncInfo(`Utilisation de ${updateData.serverImageIds.length} IDs serveur préalablement uploadés`, {
            deliveryId: updateData.deliveryId,
            userEmail,
            serverImageIds: updateData.serverImageIds,
            deliveryInfo: deliveryInfo
          });
        }

        // Ajouter consignes_rendues_v2 seulement si présent
        if (updateData.consignes_rendues_v2 !== undefined) {
          updatePayload.consignes_rendues_v2 = updateData.consignes_rendues_v2;
        }

        logService.syncInfo(`Envoi de la mise à jour ${updateData.id} pour la livraison ${updateData.deliveryId}`, {
          deliveryId: updateData.deliveryId,
          status: updateData.status,
          imageCount: serverImageIds.length,
          payload: updatePayload,
          userEmail,
          deliveryInfo: deliveryInfo,
          failedImagesCount: failedImageIds.length,
          timestamp: new Date().toISOString()
        });

        // Utiliser l'endpoint approprié avec PATCH sur /livraison/delivery/
        const response = await axios.patch(`/livraison/delivery/${updateData.deliveryId}/`, updatePayload);

        // Récupérer la réponse du serveur
        const responseData = response.data;

        // Marquer la mise à jour comme synchronisée
        updateData.synced = true;
        updateData.syncedAt = new Date().toISOString();
        updateData.serverResponse = {
          status: response.status,
          data: responseData
        };

        // Mettre à jour l'état stocké
        await dataService.storePendingUpdate(updateData);

        // Mettre à jour le cache de la livraison si les données de la livraison sont dans la réponse
        if (responseData.delivery && responseData.delivery.id) {
          await dataService.saveDelivery(responseData.delivery.id, responseData.delivery);
          
          // Mettre à jour notre objet deliveryInfo pour les logs suivants
          if (responseData.delivery) {
            deliveryInfo = {
              id: responseData.delivery.id,
              address: responseData.delivery.address,
              client: responseData.delivery.client,
              status: responseData.delivery.status,
              tourneeId: responseData.delivery.tournee_id,
              reference: responseData.delivery.reference,
              currentServiceStatus: responseData.delivery.current_service_status,
              lastUpdate: new Date().toISOString()
            };
          }
        }

        // Log du succès
        logService.syncInfo(`Mise à jour ${updateData.id} pour la livraison ${updateData.deliveryId} synchronisée avec succès`, {
          deliveryId: updateData.deliveryId,
          status: updateData.status,
          imageCount: serverImageIds.length,
          successfulImages: serverImageIds.length,
          failedImages: failedImageIds.length,
          response: {
            status: response.status,
            message: responseData.message || 'OK',
            data: responseData
          },
          userEmail,
          deliveryInfo: deliveryInfo,
          imageErrorsSummary: imageErrorDetails.length > 0 ? imageErrorDetails : undefined
        });

        // Émettre un événement pour notifier la mise à jour réussie et rafraîchir l'interface
        this.emitDeliveryUpdatedEvent(updateData.deliveryId, updateData.status);

        return responseData;
      } else {
        // Si hors ligne, déclencher une erreur
        throw new Error("Impossible de synchroniser hors ligne");
      }
    } catch (error) {
      // Récupérer l'email même en cas d'erreur
      const userEmail = localStorage.getItem('email') || 'email_non_disponible';
      
      // Récupérer des détails sur l'erreur réseau
      const networkDetails = {
        online: navigator.onLine,
        connection: navigator.connection ? {
          effectiveType: navigator.connection.effectiveType,
          downlink: navigator.connection.downlink,
          rtt: navigator.connection.rtt,
          saveData: navigator.connection.saveData
        } : null
      };
      
      // Analyser l'erreur pour obtenir plus de détails
      const errorDetails = {
        name: error.name,
        message: error.message,
        code: error.code,
        stack: error.stack,
        isAxiosError: error.isAxiosError || false,
        request: error.request ? {
          url: error.config?.url,
          method: error.config?.method,
          headers: error.config?.headers,
          timeout: error.config?.timeout
        } : null,
        response: error.response ? {
          status: error.response.status,
          statusText: error.response.statusText,
          headers: error.response.headers,
          data: error.response.data
        } : null
      };
      
      // Essayer de récupérer les informations de la livraison pour enrichir les logs
      let deliveryInfo = null;
      if (updateData && updateData.deliveryId) {
        try {
          const delivery = await dataService.getDelivery(updateData.deliveryId);
          if (delivery) {
            deliveryInfo = {
              id: delivery.id,
              address: delivery.address,
              client: delivery.client,
              status: delivery.status,
              tourneeId: delivery.tournee_id,
              reference: delivery.reference,
              currentServiceStatus: delivery.current_service_status,
              plannedTime: delivery.planned_time,
              deliveryNote: delivery.delivery_note,
              lastUpdate: delivery.last_update
            };
          }
        } catch (deliveryFetchError) {
          console.warn(`Impossible de récupérer les infos de livraison: ${deliveryFetchError.message}`);
        }
      }
      
      // En cas d'erreur, marquer la mise à jour pour une nouvelle tentative
      try {
        // Calculer le délai avant la prochaine tentative (backoff exponentiel limité)
        const attempts = (updateData.syncAttempts || 0) + 1;
        const backoffMinutes = Math.min(30, 5 * Math.pow(2, attempts - 1)); // 5, 10, 20, 30 min max
        const nextAttemptTime = new Date(Date.now() + backoffMinutes * 60 * 1000);
        
        // Mettre à jour les informations
        updateData.syncAttempts = attempts;
        updateData.lastSyncAttempt = new Date().toISOString();
        updateData.nextAttemptAfter = nextAttemptTime.toISOString();
        updateData.lastError = {
          message: error.message,
          status: error.response?.status,
          statusText: error.response?.statusText,
          time: new Date().toISOString(),
          type: error.name || 'UnknownError',
          isNetworkError: !navigator.onLine || error.message.includes('Network Error'),
          responseData: error.response?.data
        };
        
        // Enregistrer les informations de tentative
        await dataService.storePendingUpdate(updateData);
        
        // Planifier une nouvelle tentative automatique
        this.scheduleUpdateRetry(updateData.id, updateData);
      } catch (storageError) {
        logService.syncError(`Impossible de mettre à jour les informations de tentative pour ${updateData.id}`, {
          deliveryId: updateData.deliveryId,
          error: storageError.message,
          userEmail,
          originalError: error.message,
          deliveryInfo
        });
      }
      
      // Log de l'erreur
      logService.syncError(`Erreur lors de la synchronisation de la mise à jour ${updateData.id}`, {
        deliveryId: updateData.deliveryId,
        error: error.message,
        status: error.response?.status,
        stack: error.stack,
        userEmail,
        networkDetails,
        errorDetails,
        deliveryInfo,
        updateData: {
          id: updateData.id,
          status: updateData.status,
          timestamp: updateData.timestamp,
          imageCount: updateData.imageIds?.length || 0,
          attempts: updateData.syncAttempts
        },
        deviceInfo: {
          userAgent: navigator.userAgent,
          platform: navigator.platform,
          language: navigator.language,
          memory: navigator.deviceMemory,
          vendor: navigator.vendor
        },
        timestamp: new Date().toISOString()
      });
      
      // Propager l'erreur
      throw error;
    }
  }

  // Méthode pour planifier une réessai de synchronisation d'une mise à jour
  scheduleUpdateRetry(updateId, updateData) {
    // Nettoyer tout timeout existant pour cette mise à jour
    if (this.syncRetryTimeouts[updateId]) {
      clearTimeout(this.syncRetryTimeouts[updateId]);
    }
    
    // Calculer le délai de backoff (30s, 2min, 5min, 10min, 15min max)
    const attempts = updateData.syncAttempts || 0;
    const baseDelay = 30 * 1000; // 30 secondes
    const maxDelay = 15 * 60 * 1000; // 15 minutes
    
    // Formule de backoff exponentiel avec plafond
    const delay = Math.min(maxDelay, baseDelay * Math.pow(2, attempts - 1));
    
    // Essayer de récupérer les informations de la livraison pour enrichir les logs
    const getDeliveryInfo = async (deliveryId) => {
      try {
        const delivery = await dataService.getDelivery(deliveryId);
        if (delivery) {
          return {
            id: delivery.id,
            address: delivery.address,
            client: delivery.client,
            status: delivery.status,
            tourneeId: delivery.tournee_id,
            reference: delivery.reference,
            currentServiceStatus: delivery.current_service_status
          };
        }
      } catch (error) {
        // Simplement ignorer l'erreur
        console.warn(`Impossible de récupérer les infos de livraison pour ${deliveryId}: ${error.message}`);
      }
      return null;
    };
    
    // Planifier la nouvelle tentative
    this.syncRetryTimeouts[updateId] = setTimeout(async () => {
      // Vérifier si l'appareil est en ligne
      if (navigator.onLine) {
        try {
          // Récupérer les données fraîches depuis le stockage
          const pendingUpdates = await dataService.getPendingUpdates();
          const freshUpdateData = pendingUpdates.find(update => update.id === updateId);
          
          if (freshUpdateData && !freshUpdateData.synced) {
            // Récupérer les informations de la livraison
            const deliveryInfo = await getDeliveryInfo(freshUpdateData.deliveryId);
            
            // Calculer le temps écoulé depuis la dernière tentative
            let timeElapsed = 'inconnu';
            if (freshUpdateData.lastSyncAttempt) {
              const lastAttempt = new Date(freshUpdateData.lastSyncAttempt);
              const now = new Date();
              const diffMs = now - lastAttempt;
              timeElapsed = Math.round(diffMs / 1000 / 60) + ' minutes';
            }
            
            logService.syncInfo(`Tentative automatique de synchronisation de la mise à jour ${updateId}`, {
              deliveryId: freshUpdateData.deliveryId,
              attempts: freshUpdateData.syncAttempts,
              lastError: freshUpdateData.lastError,
              deliveryInfo,
              timeElapsedSinceLastAttempt: timeElapsed,
              updateStatus: freshUpdateData.status,
              timestamp: new Date().toISOString(),
              networkStatus: {
                online: navigator.onLine,
                connection: navigator.connection ? {
                  effectiveType: navigator.connection.effectiveType,
                  downlink: navigator.connection.downlink
                } : null
              }
            });
            
            // Tenter de synchroniser la mise à jour
            await this.syncUpdate(freshUpdateData);
          }
        } catch (error) {
          // Essayer de récupérer les données pour enrichir les logs
          const pendingUpdates = await dataService.getPendingUpdates();
          const freshUpdateData = pendingUpdates.find(update => update.id === updateId);
          
          // Récupérer les informations de la livraison si possible
          let deliveryInfo = null;
          if (freshUpdateData && freshUpdateData.deliveryId) {
            deliveryInfo = await getDeliveryInfo(freshUpdateData.deliveryId);
          }
          
          // Récupérer des détails sur l'erreur de réseau
          const networkDetails = {
            online: navigator.onLine,
            connection: navigator.connection ? {
              effectiveType: navigator.connection.effectiveType,
              downlink: navigator.connection.downlink,
              rtt: navigator.connection.rtt,
              saveData: navigator.connection.saveData
            } : null
          };
          
          logService.syncError(`Échec de la nouvelle tentative de synchronisation pour la mise à jour ${updateId}`, {
            error: error.message,
            deliveryId: freshUpdateData?.deliveryId,
            attempts: freshUpdateData?.syncAttempts,
            status: error.response?.status,
            statusText: error.response?.statusText,
            responseData: error.response?.data,
            errorType: error.name || 'UnknownError',
            deliveryInfo,
            updateStatus: freshUpdateData?.status,
            networkDetails,
            timestamp: new Date().toISOString(),
            errorStack: error.stack
          });
          
          // Vérifier si nous atteignons le nombre maximum de tentatives (10)
          if (freshUpdateData && freshUpdateData.syncAttempts >= 10) {
            logService.syncWarning(`Abandon des tentatives de synchronisation pour la mise à jour ${updateId} après ${freshUpdateData.syncAttempts} échecs`, {
              deliveryId: freshUpdateData.deliveryId,
              updateStatus: freshUpdateData.status,
              lastError: freshUpdateData.lastError,
              deliveryInfo,
              requiresManualSync: true
            });
            
            // Marquer que cette mise à jour nécessite une intervention manuelle
            freshUpdateData.requiresManualSync = true;
            await dataService.storePendingUpdate(freshUpdateData);
          }
          // Ne rien faire d'autre, une nouvelle tentative sera planifiée par syncUpdate si nécessaire
        }
      } else {
        // Loguer que nous ne pouvons pas synchroniser car hors ligne
        logService.syncWarning(`Tentative de synchronisation de mise à jour ${updateId} impossible car hors ligne`, {
          deliveryId: updateData.deliveryId,
          attempts: updateData.syncAttempts,
          nextRetryScheduled: true
        });
        
        // Replanifier pour plus tard (délai fixe de 5 minutes quand hors ligne)
        setTimeout(() => {
          delete this.syncRetryTimeouts[updateId]; // Supprimer d'abord
          this.scheduleUpdateRetry(updateId, updateData); // Puis replanifier
        }, 5 * 60 * 1000); // 5 minutes
      }
      
      // Supprimer cette entrée du tableau de timeouts
      delete this.syncRetryTimeouts[updateId];
    }, delay);
    
    // Récupérer les informations de l'erreur précédente pour le log
    const lastErrorInfo = updateData.lastError ? {
      message: updateData.lastError.message,
      status: updateData.lastError.status,
      type: updateData.lastError.type || 'UnknownError',
      time: updateData.lastError.time
    } : 'Aucune erreur précédente';
    
    logService.syncInfo(`Planification de nouvelle tentative pour la mise à jour ${updateId} dans ${delay/1000}s`, {
      deliveryId: updateData.deliveryId,
      attempts: updateData.syncAttempts,
      lastError: lastErrorInfo,
      plannedRetryTime: new Date(Date.now() + delay).toISOString(),
      status: updateData.status,
      imageIds: updateData.imageIds,
      imageCount: updateData.imageIds?.length || 0
    });
  }

  // Méthode pour émettre un événement de mise à jour de livraison
  emitDeliveryUpdatedEvent(deliveryId, status) {
    try {
      // Déterminer si c'est une shop_delivery
      const isShopDelivery = String(deliveryId).includes('shop_') || 
                            this.shopDeliveryRegex.test(String(deliveryId));
      
      // Nettoyer l'ID si nécessaire
      let cleanId = deliveryId;
      if (isShopDelivery && String(deliveryId).startsWith('shop_')) {
        cleanId = String(deliveryId).replace('shop_', '');
      }
      
      // Créer l'événement avec des détails
      const event = new CustomEvent('delivery-updated', {
        detail: {
          deliveryId: cleanId,
          status: status,
          shop: isShopDelivery,
          source: isShopDelivery ? 'shop_delivery' : 'delivery',
          timestamp: Date.now()
        }
      });
      
      // Émettre l'événement
      window.dispatchEvent(event);
      document.dispatchEvent(event);
      
      // Log
      logService.syncInfo(`Événement delivery-updated émis pour la livraison ${deliveryId}`, {
        status: status,
        isShopDelivery: isShopDelivery
      });
    } catch (error) {
      logService.syncError(`Erreur lors de l'émission de l'événement delivery-updated`, {
        deliveryId,
        status,
        error: error.message
      });
    }
  }

  // Méthode pour mettre à jour les données locales d'une livraison dans une tournée
  async updateDeliveryInCurrentTournee(deliveryId, updateData) {
    try {
      // Mettre à jour la livraison dans la tournée via dataService
      const updated = await dataService.updateDeliveryInTournee(deliveryId, updateData, 'current');
      
      // Si la mise à jour a réussi, émettre un événement pour mettre à jour l'UI
      if (updated) {
        this.emitDeliveryUpdatedEvent(deliveryId, updateData.status);
      }
      
      return updated;
    } catch (error) {
      logService.error('Erreur lors de la mise à jour de la livraison dans la tournée courante', {
        deliveryId, 
        error: error.message
      });
      return false;
    }
  }

  // Méthode pour mettre en cache une livraison
  async cacheDelivery(deliveryId, deliveryData) {
    try {
      return await dataService.saveDelivery(deliveryId, deliveryData);
    } catch (error) {
      logService.error('Erreur lors de la mise en cache de la livraison', {
        deliveryId, 
        error: error.message
      });
      return null;
    }
  }

  // Méthode pour mettre en cache une livraison shop
  async cacheShopDelivery(deliveryId, deliveryData) {
    try {
      return await dataService.saveShopDelivery(deliveryId, deliveryData);
    } catch (error) {
      logService.error('Erreur lors de la mise en cache de la livraison shop', {
        deliveryId, 
        error: error.message
      });
      return null;
    }
  }

  // Méthode pour nettoyer le cache des livraisons
  async cleanupDeliveryCache() {
    try {
      const stats = await dataService.cleanupCache();
      logService.info('Nettoyage du cache terminé', { stats });
      return stats;
    } catch (error) {
      logService.error('Erreur lors du nettoyage du cache', { error: error.message });
      return { deliveries: 0, tournees: 0 };
    }
  }

  // Méthode pour récupérer une livraison depuis le cache
  async getCachedDelivery(deliveryId) {
    try {
      const delivery = await dataService.getDelivery(deliveryId);
      
      if (!delivery) {
        logService.debug(`Livraison ${deliveryId} non trouvée dans le cache`);
        return null;
      }
      
      return delivery;
    } catch (error) {
      logService.error('Erreur lors de la récupération de la livraison depuis le cache', {
        deliveryId, 
        error: error.message
      });
      return null;
    }
  }
  
  // Méthode pour récupérer une livraison shop depuis le cache
  async getCachedShopDelivery(deliveryId) {
    try {
      const delivery = await dataService.getShopDelivery(deliveryId);
      
      if (!delivery) {
        logService.debug(`Livraison shop ${deliveryId} non trouvée dans le cache`);
        return null;
      }
      
      return delivery;
    } catch (error) {
      logService.error('Erreur lors de la récupération de la livraison shop depuis le cache', {
        deliveryId, 
        error: error.message
      });
      return null;
    }
  }

  // Méthode pour stocker une mise à jour en attente
  async storePendingUpdate(updateData) {
    try {
      return await dataService.storePendingUpdate(updateData);
    } catch (error) {
      logService.error('Erreur lors du stockage de la mise à jour en attente', {
        deliveryId: updateData.deliveryId,
        error: error.message
      });
      throw error;
    }
  }

  // Méthode pour stocker une image en attente
  async storePendingImage(file, metadata) {
    try {
      // Vérifier que le fichier est bien un objet File ou Blob
      if (!(file instanceof Blob || file instanceof File)) {
        throw new Error('Le fichier doit être une instance de File ou Blob');
      }
      
      // Vérifier que les métadonnées contiennent l'ID de livraison
      if (!metadata || !metadata.deliveryId) {
        throw new Error('ID de livraison requis dans les métadonnées');
      }
      
      // Déterminer le type d'image selon le contexte
      let type = metadata.type || 'delivery';
      const deliveryContext = metadata.deliveryContext || 'default';
      
      // Si le contexte contient "shop", marquer explicitement comme shop_delivery
      if (deliveryContext.includes('shop') && type !== 'shop_delivery') {
        type = 'shop_delivery';
        console.log(`Type d'image corrigé en 'shop_delivery' basé sur le contexte: ${deliveryContext}`);
      }
      
      // Générer un ID unique avec préfixe adapté au type
      const prefix = type === 'shop_delivery' ? 'shop_img_' : 'img_';
      const imageId = metadata.id || `${prefix}${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;
      
      // Lire le fichier pour le stocker sous forme d'ArrayBuffer
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = async () => {
          try {
            // Créer l'objet avec les métadonnées et le ArrayBuffer
            const imageData = {
              id: imageId,
              file: reader.result, // ArrayBuffer
              fileName: metadata.fileName || file.name || `image_${Date.now()}.jpg`,
              fileType: metadata.fileType || file.type || 'image/jpeg',
              size: file.size,
              deliveryId: metadata.deliveryId,
              deliveryContext: deliveryContext,
              type: type,
              timestamp: new Date().toISOString(),
              synced: false,
              syncAttempts: 0,
              createdAt: new Date().toISOString()
            };
            
            // Stocker l'image dans le stockage local
            await dataService.storePendingImage(imageId, imageData);
            
            // Stocker également une copie Blob de l'image pour un accès direct
            try {
              const blob = new Blob([reader.result], { type: imageData.fileType });
              // Créer un objet File à partir du Blob
              const fileObject = new File([blob], imageData.fileName, {
                type: imageData.fileType,
                lastModified: Date.now()
              });
              
              // Créer un cache de l'image avec l'objet File pour une utilisation ultérieure
              const imageCache = {
                ...imageData,
                fileObject: fileObject // Stocker l'objet File séparément
              };
              
              // Utiliser l'instance imageObjectsStore initialisée dans le constructeur
              if (this.imageObjectsStore) {
                await this.imageObjectsStore.setItem(imageId, imageCache);
                
                logService.syncInfo(`Image ${imageId} et son objet File stockés avec succès`, {
                  deliveryId: metadata.deliveryId,
                  fileSize: file.size,
                  type: type,
                  context: deliveryContext
                });
              } else {
                // Fallback si imageObjectsStore n'est pas disponible
                const imageStoreInstance = localforage.createInstance({
                  name: 'images',
                  storeName: 'image_objects'
                });
                
                await imageStoreInstance.setItem(imageId, imageCache);
                
                logService.syncInfo(`Image ${imageId} stockée avec instance temporaire`, {
                  deliveryId: metadata.deliveryId,
                  fileSize: file.size,
                  type: type
                });
              }
            } catch (cacheError) {
              logService.syncWarning(`Impossible de stocker l'objet File de l'image ${imageId}`, {
                error: cacheError.message,
                deliveryId: metadata.deliveryId
              });
              // Continuer malgré l'erreur, ce n'est qu'un cache supplémentaire
            }
            
            resolve(imageId);
          } catch (error) {
            reject(error);
          }
        };
        
        reader.onerror = () => {
          reject(new Error('Échec de la lecture du fichier image'));
        };
        
        // Lire le fichier en tant qu'ArrayBuffer
        reader.readAsArrayBuffer(file);
      });
    } catch (error) {
      logService.error('Erreur lors du stockage de l\'image en attente', {
        deliveryId: metadata?.deliveryId,
        error: error.message,
        stack: error.stack
      });
      throw error;
    }
  }

  // Méthode pour synchroniser toutes les mises à jour et images en attente
  async syncAll() {
    if (this.syncInProgress) {
      logService.syncWarning('Une synchronisation est déjà en cours');
      return { success: false, reason: 'sync_in_progress' };
    }

    if (!navigator.onLine) {
      logService.syncWarning('Impossible de synchroniser : appareil hors ligne');
      return { success: false, reason: 'offline' };
    }
    
    this.syncInProgress = true;
    this.lastSyncTime = Date.now();
    
    const results = { 
      success: 0, 
      failed: 0, 
      errors: [], 
      updatesProcessed: 0, 
      imagesProcessed: 0,
      shopDeliverySuccess: 0,
      shopDeliveryFailed: 0,
      imagesSynced: 0,
      shopImagesSynced: 0,
      alreadySyncedSkipped: 0
    };

    try {
      logService.syncInfo('Début de la synchronisation complète');
      
      // Nettoyer d'abord le cache des objets File pour éviter des problèmes de mémoire
      if (dataService.clearFileObjectsCache) {
        dataService.clearFileObjectsCache();
        console.log('Cache mémoire des objets File vidé avant synchronisation');
      }

      // Récupérer les mises à jour en attente avec gestion robuste des erreurs IndexedDB
      const pendingData = await this.exportPendingData();
      
      // Vérifier et afficher les données récupérées pour diagnostic
      console.log('Données en attente de synchronisation:', {
        totalImages: pendingData.images?.length || 0,
        totalUpdates: pendingData.updates?.length || 0
      });
      
      // Extraire toutes les images en attente
      let pendingImages = pendingData.images || [];
      
      // OPTIMISATION: Filtrer d'abord pour retirer les images déjà synchronisées
      const alreadySyncedImages = pendingImages.filter(img => img && img.synced === true && img.serverImageId);
      pendingImages = pendingImages.filter(img => !(img && img.synced === true && img.serverImageId));
      
      // Purger immédiatement les images déjà synchronisées pour éviter des tentatives redondantes
      if (alreadySyncedImages.length > 0) {
        console.log(`Suppression de ${alreadySyncedImages.length} images déjà synchronisées`);
        results.alreadySyncedSkipped = alreadySyncedImages.length;
        
        for (const img of alreadySyncedImages) {
          try {
            await dataService.removePendingImage(img.id);
            console.log(`Image ${img.id} déjà synchronisée supprimée du stockage local`);
          } catch (error) {
            console.warn(`Erreur lors de la suppression de l'image synchronisée ${img.id}:`, error);
          }
        }
      }
      
      // Synchroniser les autres images
      if (pendingImages.length > 0) {
        // Diagnostic des images récupérées
        pendingImages.forEach(img => {
          console.log(`Image en attente: ${img.id}`, {
            type: img.type || 'non spécifié',
            deliveryId: img.deliveryId,
            deliveryContext: img.deliveryContext || img.context,
            synced: img.synced || false,
            needsSync: img.needsSync || false,
            queuedOffline: img.queuedOffline || false
          });
        });
        
        logService.syncInfo(`Synchronisation de ${pendingImages.length} images en attente`);
        
        // Séparer les images par type (shop_delivery ou delivery standard)
        // Utiliser une approche plus explicite pour détecter les shop_delivery images
        const shopDeliveryImages = [];
        const standardDeliveryImages = [];
        
        for (const img of pendingImages) {
          // Si déjà synchronisée, ignorer
          if (img.synced === true) {
            continue;
          }
          
          // Amélioration de la détection des images shop_delivery
          const isShopDelivery = (
            img.type === 'shop_delivery' || 
            img.deliveryContext?.includes('shop') || 
            img.context?.includes('shop') ||
            (img.deliveryId && img.deliveryId.toString().startsWith('shop_')) ||
            (img.id && (
              img.id.startsWith('shop_img_') || 
              img.id.includes('_shop_') ||
              img.id.includes('shop_delivery')
            ))
          );
          
          // S'assurer que les métadonnées sont complètes pour les images shop
          if (isShopDelivery && img.type !== 'shop_delivery') {
            img.type = 'shop_delivery';
            
            // Définir le contexte si absent
            if (!img.deliveryContext && !img.context) {
              img.deliveryContext = 'shop_photo';
            }
            
            // S'assurer que needsSync est défini
            img.needsSync = true;
            
            // Mettre à jour dans le stockage local
            await dataService.updatePendingImage(img.id, img);
          }
          
          console.log(`Classification de l'image ${img.id}:`, {
            typeAttribué: isShopDelivery ? 'shop_delivery' : 'standard',
            typeOriginal: img.type,
            context: img.deliveryContext || img.context,
            queuedOffline: img.queuedOffline || false
          });
          
          if (isShopDelivery) {
            shopDeliveryImages.push(img);
          } else {
            standardDeliveryImages.push(img);
          }
        }
        
        logService.syncInfo(`Images à synchroniser: ${standardDeliveryImages.length} standard, ${shopDeliveryImages.length} shop`, {
          shopImages: shopDeliveryImages.map(img => ({
            id: img.id, 
            type: img.type,
            context: img.deliveryContext
          }))
        });
        
        // Synchroniser les images des livraisons standard d'abord
        for (const image of standardDeliveryImages) {
          if (!image.synced) {
            try {
              results.imagesProcessed++;
              console.log(`Tentative de synchronisation d'image standard: ${image.id}`);
              const serverId = await this.syncImage(image);
              if (serverId) {
                results.imagesSynced++;
                console.log(`Image standard ${image.id} synchronisée avec succès, serverId: ${serverId}`);
              }
            } catch (error) {
              logService.syncError(`Échec de synchronisation d'image standard ${image.id}`, {
                error: error.message
              });
            }
          }
        }
        
        // Puis synchroniser les images des shop deliveries
        for (const image of shopDeliveryImages) {
          if (!image.synced) {
            try {
              results.imagesProcessed++;
              console.log(`Tentative de synchronisation d'image shop: ${image.id}`, {
                deliveryId: image.deliveryId,
                context: image.deliveryContext,
                type: image.type
              });
              
              // Corriger les métadonnées si nécessaire
              const correctedImage = { ...image };
              if (!correctedImage.type) {
                correctedImage.type = 'shop_delivery';
              }
              
              const serverId = await this.syncShopDeliveryImage(correctedImage);
              if (serverId) {
                results.shopImagesSynced++;
                console.log(`Image shop ${image.id} synchronisée avec succès, serverId: ${serverId}`);
              } else {
                console.error(`Échec de synchronisation d'image shop ${image.id} - pas de serverId retourné`);
              }
            } catch (error) {
              console.error(`Exception lors de la synchronisation d'image shop ${image.id}:`, error);
              logService.syncError(`Échec de synchronisation d'image shop ${image.id}`, {
                error: error.message,
                errorStack: error.stack,
                imageData: {
                  id: image.id,
                  type: image.type,
                  deliveryContext: image.deliveryContext
                }
              });
            }
          }
        }
      }
      
      // Ensuite, séparer les mises à jour entre deliveries standard et shop_deliveries
      const pendingUpdates = pendingData.updates || [];
      const pendingDeliveryUpdates = pendingUpdates.filter(update => update.type !== 'shop_delivery' || !update.type);
      const pendingShopDeliveryUpdates = pendingUpdates.filter(update => update.type === 'shop_delivery');
      
      if (pendingUpdates.length === 0) {
        logService.syncInfo('Aucune mise à jour en attente à synchroniser');
      } else {
        logService.syncInfo(`${pendingUpdates.length} mises à jour en attente à synchroniser (${pendingDeliveryUpdates.length} standard, ${pendingShopDeliveryUpdates.length} shop)`);
      
        // ÉTAPE 1 : Synchroniser d'abord les deliveries standard
        if (pendingDeliveryUpdates.length > 0) {
          // Trier les mises à jour par date (les plus anciennes d'abord)
          pendingDeliveryUpdates.sort((a, b) => {
            const dateA = new Date(a.timestamp || a.createdAt || 0);
            const dateB = new Date(b.timestamp || b.createdAt || 0);
            return dateA.getTime() - dateB.getTime();
          });
          
          // Synchroniser les mises à jour une par une
          for (const update of pendingDeliveryUpdates) {
            if (!update.synced) {
              try {
                results.updatesProcessed++;
                await this.syncUpdate(update);
                results.success++;
                
                // Supprimer la mise à jour du stockage local après succès avec gestion robuste des erreurs
                await this.tryWithIndexedDBFallback(
                  async () => await dataService.removePendingUpdate(update.id),
                  async () => {
                    // Fallback: supprimer de localStorage
                    try {
                      const pendingUpdateKey = `pendingUpdate_${update.id}`;
                      localStorage.removeItem(pendingUpdateKey);
                      return true;
                    } catch (e) {
                      console.warn(`Impossible de supprimer la mise à jour du localStorage: ${e.message}`);
                      return false;
                    }
                  }
                );
                
                // Supprimer tout timeout de réessai pour cette mise à jour
                if (this.syncRetryTimeouts[update.id]) {
                  clearTimeout(this.syncRetryTimeouts[update.id]);
                  delete this.syncRetryTimeouts[update.id];
                }
              } catch (error) {
                results.failed++;
                results.errors.push({
                  type: 'update',
                  id: update.id,
                  deliveryId: update.deliveryId,
                  error: error.message,
                  status: error.response?.status
                });
              }
            } else {
              // Si déjà synchronisée, supprimer simplement du stockage avec gestion robuste des erreurs
              await this.tryWithIndexedDBFallback(
                async () => await dataService.removePendingUpdate(update.id),
                async () => {
                  // Fallback: supprimer de localStorage
                  try {
                    const pendingUpdateKey = `pendingUpdate_${update.id}`;
                    localStorage.removeItem(pendingUpdateKey);
                    return true;
                  } catch (e) {
                    console.warn(`Impossible de supprimer la mise à jour du localStorage: ${e.message}`);
                    return false;
                  }
                }
              );
            }
          }
        }

        // ÉTAPE 2 : Synchroniser ensuite les shop_deliveries
        if (pendingShopDeliveryUpdates.length > 0) {
          // Créer un ensemble pour suivre les livraisons déjà traitées
          const processedShopDeliveryIds = new Set();
          
          // Trier les mises à jour par date (les plus anciennes d'abord)
          pendingShopDeliveryUpdates.sort((a, b) => {
            const dateA = new Date(a.timestamp || a.createdAt || 0);
            const dateB = new Date(b.timestamp || b.createdAt || 0);
            return dateA.getTime() - dateB.getTime();
          });
          
          // Synchroniser les mises à jour une par une, en évitant les doublons
          for (const update of pendingShopDeliveryUpdates) {
            // Vérifier si cette livraison a déjà été traitée dans cette session
            if (processedShopDeliveryIds.has(update.deliveryId)) {
              console.log(`Mise à jour shop ${update.id} pour la livraison ${update.deliveryId} ignorée (déjà traitée)`);
              // Marquer comme synchronisée pour la supprimer du stockage local
              update.synced = true;  // Correction : updateData -> update
              await dataService.storePendingUpdate(update);
              await dataService.removePendingUpdate(update.id);
              continue;
            }
            
            // Ajouter l'ID à l'ensemble des livraisons traitées
            processedShopDeliveryIds.add(update.deliveryId);
            
            if (!update.synced) {
              try {
                results.updatesProcessed++;
                await this.syncShopDeliveryUpdate(update);
                results.shopDeliverySuccess++;
                
                // Supprimer la mise à jour du stockage local après succès
                await dataService.removePendingUpdate(update.id);
                
                // Supprimer aussi toutes les autres mises à jour pour cette même livraison
                // qui seraient encore en attente (évite la redondance)
                for (const otherUpdate of pendingShopDeliveryUpdates) {
                  if (otherUpdate.id !== update.id && otherUpdate.deliveryId === update.deliveryId && !otherUpdate.synced) {
                    console.log(`Nettoyage supplémentaire: mise à jour ${otherUpdate.id} pour livraison ${otherUpdate.deliveryId} marquée comme synchronisée`);
                    otherUpdate.synced = true;
                    await dataService.storePendingUpdate(otherUpdate);
                    await dataService.removePendingUpdate(otherUpdate.id);
                  }
                }
                
                // Forcer un nettoyage immédiat des images associées à cette livraison
                try {
                  const pendingImages = await dataService.getPendingImages();
                  const relatedImages = pendingImages.filter(img => img.deliveryId === update.deliveryId);
                  
                  if (relatedImages.length > 0) {
                    console.log(`Nettoyage de ${relatedImages.length} images associées à la livraison ${update.deliveryId}`);
                    
                    for (const img of relatedImages) {
                      // Marquer l'image comme synchronisée si elle a un ID serveur
                      if (img.serverImageId) {
                        img.synced = true;
                        await dataService.updatePendingImage(img.id, img);
                      }
                      
                      // Supprimer l'image du stockage local
                      await dataService.removePendingImage(img.id);
                    }
                  }
                } catch (cleanupError) {
                  console.warn(`Erreur lors du nettoyage des images associées à la livraison ${update.deliveryId}:`, cleanupError);
                }
              } catch (error) {
                results.shopDeliveryFailed++;
                results.errors.push({
                  id: update.id,
                  deliveryId: update.deliveryId,
                  type: 'shop_delivery',
                  error: error.message,
                  status: error.response?.status
                });
                // On continue avec les suivantes
              }
            } else {
              // Si déjà synchronisée, supprimer du stockage
              await dataService.removePendingUpdate(update.id);
            }
          }
        }
      }

      // Nettoyer les images qui ne sont plus associées à des mises à jour
      try {
        await this.cleanupPendingImages();
      } catch (cleanupError) {
        logService.syncWarning('Erreur lors du nettoyage des images en attente, continuons quand même', {
          error: cleanupError.message
        });
      }
          
      logService.syncInfo('Synchronisation terminée', {
        success: results.success + results.shopDeliverySuccess,
        failed: results.failed + results.shopDeliveryFailed,
        updatesProcessed: results.updatesProcessed,
        imagesProcessed: results.imagesProcessed,
        imagesSynced: results.imagesSynced,
        shopImagesSynced: results.shopImagesSynced
      });
      
      // Nettoyer les images qui ont été correctement synchronisées
      // (réduire l'empreinte de stockage et améliorer les performances)
      const cleanupResults = await this.cleanupSyncedImages();
      logService.syncInfo(`Nettoyage après synchronisation: ${cleanupResults.deleted} images supprimées, ${cleanupResults.kept} images conservées`);
      
      // Nettoyer également les autres données (tournées anciennes, etc.)
      await this.cleanup();
      
      return {
        success: true,
        stats: {
          success: results.success + results.shopDeliverySuccess,
          failed: results.failed + results.shopDeliveryFailed,
          total: results.updatesProcessed,
          imagesSynced: results.imagesSynced + results.shopImagesSynced,
          standardImages: results.imagesSynced,
          shopImages: results.shopImagesSynced,
          imagesDeleted: cleanupResults.deleted
        }
      };
    } catch (error) {
      // Gestion d'erreur existante...
      logService.syncError('Erreur grave lors de la synchronisation', {
        error: error.message,
        stack: error.stack,
        userAgent: navigator.userAgent,
        platform: navigator.platform
      });
      
      return {
        success: false,
        reason: 'error',
        error: error.message
      };
    } finally {
      this.syncInProgress = false;
    }
  }

  // Méthode pour nettoyer les images en attente
  async cleanupPendingImages() {
    try {
      // Récupérer toutes les images en attente avec gestion d'erreur
      let pendingImages = [];
      try {
        pendingImages = await this.tryWithIndexedDBFallback(
          async () => await dataService.getPendingImages(),
          async () => {
            // Fallback: essayer de récupérer depuis localStorage
            const images = [];
            for (let i = 0; i < localStorage.length; i++) {
              const key = localStorage.key(i);
              if (key.startsWith('pendingImages_')) {
                try {
                  const imagesData = JSON.parse(localStorage.getItem(key));
                  if (Array.isArray(imagesData)) {
                    images.push(...imagesData);
                  }
                } catch (e) {
                  console.warn("Impossible de parser les données d'images:", e);
                }
              }
            }
            return images;
          }
        );
      } catch (fetchError) {
        // S'il y a une erreur lors de la récupération des images, log et retourner
        logService.syncError('Erreur lors de la récupération des images en attente pour nettoyage', {
          error: fetchError.message,
          stack: fetchError.stack
        });
        return { deleted: 0, processed: 0, flaggedForManualSync: 0, error: fetchError.message };
      }
      
      if (!pendingImages || pendingImages.length === 0) {
        logService.syncInfo('Aucune image en attente à nettoyer');
        return { deleted: 0, processed: 0, flaggedForManualSync: 0 };
      }
      
      // Récupérer les metadata sur les images
      const imageStats = {
        total: pendingImages.length,
        synced: pendingImages.filter(img => img && img.synced).length,
        failed: pendingImages.filter(img => img && !img.synced && img.syncAttempts && img.syncAttempts > 0).length,
        untried: pendingImages.filter(img => img && !img.synced && (!img.syncAttempts || img.syncAttempts === 0)).length,
        byType: {}
      };
      
      // Compter par type de fichier
      pendingImages.forEach(img => {
        if (img && img.fileType) {
          imageStats.byType[img.fileType] = (imageStats.byType[img.fileType] || 0) + 1;
        }
      });
      
      logService.syncInfo(`Nettoyage de ${pendingImages.length} images en attente`, {
        stats: imageStats,
        timestamp: new Date().toISOString()
      });
      
      let deleted = 0;
      let flaggedForManualSync = 0;
      const deliveryImagesCount = {}; // Pour compter les images par livraison
      const problemImages = []; // Pour stocker les infos sur les images problématiques
      
      // Pour chaque image...
      for (const image of pendingImages) {
        // Vérifier que l'image est valide
        if (!image || !image.id) {
          continue;
        }
        
        // Compter les images par livraison
        if (image.deliveryId) {
          deliveryImagesCount[image.deliveryId] = (deliveryImagesCount[image.deliveryId] || 0) + 1;
        }
        
        // Si elle est déjà marquée comme synchronisée, la supprimer
        if (image.synced && image.serverImageId) {
          try {
            await this.tryWithIndexedDBFallback(
              async () => await dataService.removePendingImage(image.id),
              async () => {
                // Fallback: supprimer de localStorage
                const pendingImagesKey = `pendingImages_${image.deliveryId}`;
                const storedData = localStorage.getItem(pendingImagesKey);
                if (storedData) {
                  let pendingImages = JSON.parse(storedData);
                  pendingImages = pendingImages.filter(img => img.id !== image.id);
                  localStorage.setItem(pendingImagesKey, JSON.stringify(pendingImages));
                }
                return true;
              }
            );
            deleted++;
          } catch (removeError) {
            logService.syncWarning(`Impossible de supprimer l'image ${image.id}`, {
              error: removeError.message
            });
          }
          
          // Nettoyer tout timeout existant
          if (this.syncRetryTimeouts[image.id]) {
            clearTimeout(this.syncRetryTimeouts[image.id]);
            delete this.syncRetryTimeouts[image.id];
          }
          
          continue;
        }
        
        // Créer un objet avec les infos pertinentes de l'image
        const imageInfo = {
          id: image.id,
          deliveryId: image.deliveryId,
          fileName: image.fileName,
          fileType: image.fileType,
          fileSize: image.size,
          timestamp: image.timestamp || image.createdAt,
          syncAttempts: image.syncAttempts || 0,
          lastError: image.lastError,
          context: image.deliveryContext
        };
          
        // Si elle a trop de tentatives échouées, marquer avec flag spécial
        if (image.syncAttempts && image.syncAttempts > 5) {
          // Marquer comme nécessitant une intervention manuelle
          const updatedImage = { 
            ...image, 
            requiresManualSync: true,
            manualSyncFlaggedAt: new Date().toISOString(),
            manualSyncReason: `Échec après ${image.syncAttempts} tentatives`
          };
          
          try {
            await this.tryWithIndexedDBFallback(
              async () => await dataService.updatePendingImage(image.id, updatedImage),
              async () => {
                // Fallback: mise à jour dans localStorage
                const pendingImagesKey = `pendingImages_${image.deliveryId}`;
                let pendingImages = [];
                const storedData = localStorage.getItem(pendingImagesKey);
                
                if (storedData) {
                  pendingImages = JSON.parse(storedData);
                  const index = pendingImages.findIndex(img => img.id === image.id);
                  if (index !== -1) {
                    pendingImages[index] = updatedImage;
                  } else {
                    pendingImages.push(updatedImage);
                  }
                } else {
                  pendingImages = [updatedImage];
                }
                
                localStorage.setItem(pendingImagesKey, JSON.stringify(pendingImages));
                return true;
              }
            );
            flaggedForManualSync++;
          } catch (updateError) {
            logService.syncWarning(`Impossible de marquer l'image ${image.id} pour intervention manuelle`, {
              error: updateError.message
            });
          }
          
          // Ajouter aux images problématiques
          problemImages.push({
            ...imageInfo,
            reason: `Échec après ${image.syncAttempts} tentatives`,
            flaggedForManualSync: true
          });
          
          logService.syncWarning(`L'image ${image.id} a été marquée pour intervention manuelle après ${image.syncAttempts} échecs`, {
            deliveryId: image.deliveryId,
            fileName: image.fileName,
            fileType: image.fileType,
            fileSize: image.size,
            lastError: image.lastError
          });
        }
        
        // Si elle est très ancienne (plus de 48 heures), la supprimer
        const timestamp = new Date(image.timestamp || image.createdAt || 0);
        const now = new Date();
        const diffHours = Math.floor((now - timestamp) / (1000 * 60 * 60));
        
        if (diffHours > 48) {
          // Ajouter aux images problématiques avant suppression
          problemImages.push({
            ...imageInfo,
            reason: `Trop ancienne (${diffHours} heures)`,
            deleted: true
          });
          
          logService.syncWarning(`Suppression de l'image ${image.id} car trop ancienne (${diffHours} heures)`, {
            deliveryId: image.deliveryId,
            fileName: image.fileName,
            fileType: image.fileType,
            fileSize: image.size,
            lastError: image.lastError,
            syncAttempts: image.syncAttempts
          });
          
          try {
            await this.tryWithIndexedDBFallback(
              async () => await dataService.removePendingImage(image.id),
              async () => {
                // Fallback: supprimer de localStorage
                const pendingImagesKey = `pendingImages_${image.deliveryId}`;
                const storedData = localStorage.getItem(pendingImagesKey);
                if (storedData) {
                  let pendingImages = JSON.parse(storedData);
                  pendingImages = pendingImages.filter(img => img.id !== image.id);
                  localStorage.setItem(pendingImagesKey, JSON.stringify(pendingImages));
                }
                return true;
              }
            );
            deleted++;
          } catch (removeError) {
            logService.syncWarning(`Impossible de supprimer l'image ancienne ${image.id}`, {
              error: removeError.message
            });
          }
          
          // Nettoyer tout timeout existant
          if (this.syncRetryTimeouts[image.id]) {
            clearTimeout(this.syncRetryTimeouts[image.id]);
            delete this.syncRetryTimeouts[image.id];
          }
        }
      }
      
      // Compter et logger les livraisons avec beaucoup d'images en attente
      const deliveriesWithManyImages = Object.keys(deliveryImagesCount)
        .filter(id => deliveryImagesCount[id] > 3)
        .map(id => ({ deliveryId: id, imageCount: deliveryImagesCount[id] }));
      
      if (deliveriesWithManyImages.length > 0) {
        logService.syncWarning(`Livraisons avec un nombre élevé d'images en attente`, {
          deliveries: deliveriesWithManyImages,
          timestamp: new Date().toISOString()
        });
      }
      
      // Log final avec statistiques complètes
      logService.syncInfo(`Résultats du nettoyage des images en attente`, {
        deleted,
        processed: pendingImages.length,
        flaggedForManualSync,
        problemImages: problemImages.length > 0 ? problemImages : undefined,
        deliveriesWithManyImages: deliveriesWithManyImages.length > 0 ? deliveriesWithManyImages : undefined,
        timestamp: new Date().toISOString()
      });
      
      return { 
        deleted, 
        processed: pendingImages.length, 
        flaggedForManualSync,
        problemImages: problemImages.length,
        totalDeliveries: Object.keys(deliveryImagesCount).length
      };
    } catch (error) {
      // Récupérer des détails sur l'erreur
      const errorDetails = {
        name: error.name,
        message: error.message,
        code: error.code,
        stack: error.stack
      };
      
      logService.syncError('Erreur lors du nettoyage des images en attente', {
        error: error.message,
        errorDetails,
        timestamp: new Date().toISOString(),
        networkStatus: {
          online: navigator.onLine,
          connection: navigator.connection ? {
            effectiveType: navigator.connection.effectiveType,
            downlink: navigator.connection.downlink
          } : null
        }
      });
      
      return { deleted: 0, processed: 0, error: error.message };
    }
  }

  // Méthode pour nettoyer les données 
  async cleanup() {
    try {
      // Nettoyer le cache de livraisons
      await this.cleanupDeliveryCache();
      
      // Nettoyer les tournées anciennes
      await this.cleanupTournees();
      
      // Supprimer les mises à jour synchronisées
      try {
        const updates = await dataService.getPendingUpdates();
        let deletedUpdates = 0;
        
        // Filtrer les mises à jour déjà synchronisées
        const syncedUpdates = updates.filter(update => update.synced);
        
        // Supprimer chaque mise à jour synchronisée
        for (const update of syncedUpdates) {
          await dataService.removePendingUpdate(update.id);
          deletedUpdates++;
        }
        
        if (deletedUpdates > 0) {
          logService.syncInfo(`${deletedUpdates} mises à jour synchronisées supprimées du stockage local`);
        }
      } catch (updateError) {
        logService.syncWarning('Erreur lors du nettoyage des mises à jour synchronisées', {
          error: updateError.message
        });
      }
      
      return true;
    } catch (error) {
      logService.error('Erreur lors du nettoyage général', { error: error.message });
      return false;
    }
  }

  // Méthode pour nettoyer les données de tournées anciennes
  async cleanupTournees() {
    try {
      // Utiliser les nouvelles méthodes spécifiques de dataService
      const keys = await dataService.getTourneeKeys();
      let deleted = 0;
      
      for (const key of keys) {
        // Ne pas supprimer la tournée courante
        if (key === 'current_tournee') continue;
        
        const tournee = await dataService.getTourneeByKey(key);
        
        // Vérifier si la tournée est ancienne (plus de 7 jours)
        if (tournee && tournee.timestamp) {
          const timestamp = new Date(tournee.timestamp);
          const now = new Date();
          const diffDays = Math.floor((now - timestamp) / (1000 * 60 * 60 * 24));
        
          if (diffDays > 7) {
            await dataService.removeTourneeByKey(key);
            deleted++;
          }
        }
      }
      
      return { deleted, processed: keys.length };
    } catch (error) {
      logService.error('Erreur lors du nettoyage des tournées', { error: error.message });
      return { deleted: 0, processed: 0 };
    }
  }

  // Méthode pour exporter les données en attente de synchronisation
  async exportPendingData() {
    try {
      // Utiliser notre méthode avec gestion robuste des erreurs IndexedDB
      return await this.tryWithIndexedDBFallback(
        async () => {
          const updates = await dataService.getPendingUpdates();
          const images = await dataService.getPendingImages();
          
          // Filtrer les updates pour ne garder que ceux qui ne sont pas synchronisés
          const pendingUpdates = updates.filter(update => !update.synced);
          
          // Filtrer les images pour ne conserver que celles qui ne sont pas synchronisées
          const pendingImages = images.filter(image => !image.synced);
          
          return {
            updates: pendingUpdates,
            images: pendingImages
          };
        },
        async () => {
          // Fallback: essayer de récupérer depuis localStorage
          try {
            const pendingUpdates = [];
            const pendingImages = [];
            
            // Parcourir localStorage pour trouver les mises à jour en attente
            for (let i = 0; i < localStorage.length; i++) {
              const key = localStorage.key(i);
              
              // Rechercher les clés qui correspondent à nos patterns de stockage fallback
              if (key.startsWith('pendingUpdate_')) {
                try {
                  const updateData = JSON.parse(localStorage.getItem(key));
                  if (updateData && !updateData.synced) {
                    pendingUpdates.push(updateData);
                  }
                } catch (e) {
                  console.warn(`Impossible de parser les données pendingUpdate: ${e.message}`);
                }
              } else if (key.startsWith('pendingImages_')) {
                try {
                  const imagesList = JSON.parse(localStorage.getItem(key));
                  if (Array.isArray(imagesList)) {
                    for (const image of imagesList) {
                      if (image && !image.synced) {
                        pendingImages.push(image);
                      }
                    }
                  }
                } catch (e) {
                  console.warn(`Impossible de parser les données pendingImages: ${e.message}`);
                }
              }
            }
            
            return {
              updates: pendingUpdates,
              images: pendingImages
            };
          } catch (e) {
            logService.syncError("Fallback localStorage pour exportPendingData a échoué", { error: e.message });
            return { updates: [], images: [] };
          }
        }
      );
    } catch (error) {
      // Gérer spécifiquement les erreurs d'IndexedDB
      if (error.name === 'UnknownError' || 
          error.message.includes('IndexedDB') || 
          error.message.includes('Connection to Indexed Database server lost')) {
        
        this.handleIndexedDBError('exportPendingData', error);
        
        // Retourner un objet vide comme fallback en dernier recours
        return { updates: [], images: [] };
      }
      
      logService.syncError('Erreur lors de l\'export des données en attente', { 
        error: error.message,
        stack: error.stack,
        userAgent: navigator.userAgent
      });
      
      return { updates: [], images: [] };
    }
  }

  // Méthode pour vérifier s'il y a des données en attente pour une livraison
  async hasPendingDataForDelivery(deliveryId) {
    if (!deliveryId) return false;
    
    try {
      // Vérifier les mises à jour en attente
      const updates = await dataService.getPendingUpdates();
      const hasPendingUpdate = updates.some(
        update => !update.synced && update.deliveryId === deliveryId
      );
      
      if (hasPendingUpdate) return true;

      // Vérifier les images en attente
      const images = await dataService.getPendingImages();
      const hasPendingImage = images.some(
        image => !image.synced && image.deliveryId === deliveryId
      );
      
      return hasPendingImage;
    } catch (error) {
      logService.error('Erreur lors de la vérification des données en attente', {
        deliveryId, 
        error: error.message
      });
      return false;
    }
  }
  
  // Variable globale pour éviter des synchronisations doubles
  #inProgressSyncs = new Set();

  // Méthode pour forcer une synchronisation avec rafraîchissement de l'interface
  async forceSyncAndRefresh() {
    try {
      console.log('Démarrage de forceSyncAndRefresh()...');
      
      // Vérifier spécifiquement les images shop_delivery en attente
      let shopImagesDetected = false;
      try {
        const pendingData = await this.exportPendingData();
        const shopImages = pendingData.images.filter(img => 
          (img.type === 'shop_delivery' || 
          img.deliveryContext?.includes('shop') || 
          img.context?.includes('shop') ||
          (img.deliveryId && img.deliveryId.toString().startsWith('shop_')) ||
          (img.id && (
            img.id.startsWith('shop_img_') || 
            img.id.includes('_shop_') ||
            img.id.includes('shop_delivery')
          ))) && 
          !img.synced
        );
        
        if (shopImages.length > 0) {
          shopImagesDetected = true;
          console.log(`${shopImages.length} images shop_delivery en attente de synchronisation détectées`);
          
          // Log détaillé pour aider au diagnostic
          shopImages.forEach(img => {
            console.log(`Image shop en attente: ${img.id}`, {
              type: img.type || 'non spécifié',
              deliveryId: img.deliveryId,
              context: img.deliveryContext || img.context,
              synced: img.synced || false,
              hasFile: !!img.file,
              hasBase64: !!img.base64Data
            });
            
            // Correction des métadonnées si nécessaire
            if (img.type !== 'shop_delivery') {
              img.type = 'shop_delivery';
              img.needsSync = true;
              if (!img.deliveryContext && !img.context) {
                img.deliveryContext = 'shop_photo';
              }
              
              // Sauvegarder les corrections
              dataService.updatePendingImage(img.id, img)
                .catch(e => console.warn(`Erreur lors de la mise à jour des métadonnées de l'image ${img.id}:`, e));
            }
          });
        }
      } catch (checkError) {
        console.warn('Erreur lors de la vérification des images shop en attente:', checkError);
      }
      
      // Nettoyer le cache des objets File pour éviter les problèmes de mémoire
      if (dataService.clearFileObjectsCache) {
        dataService.clearFileObjectsCache();
        console.log('Cache mémoire des objets File vidé avant synchronisation forcée');
      }
      
      // Effectuer la synchronisation
      const result = await this.syncAll();
      
      // Vérifier s'il reste des images shop_delivery non synchronisées
      try {
        const remainingData = await this.exportPendingData();
        const remainingShopImages = remainingData.images.filter(img => 
          (img.type === 'shop_delivery' || 
          img.deliveryContext?.includes('shop') || 
          img.context?.includes('shop') ||
          (img.deliveryId && img.deliveryId.toString().startsWith('shop_')) ||
          (img.id && (
            img.id.startsWith('shop_img_') || 
            img.id.includes('_shop_') ||
            img.id.includes('shop_delivery')
          ))) && 
          !img.synced
        );
        
        if (remainingShopImages.length > 0) {
          console.log(`${remainingShopImages.length} images shop_delivery restent non synchronisées après syncAll`);
          
          // Tentative spécifique pour ces images avec corrections
          console.log('Tentative supplémentaire pour les images shop_delivery restantes...');
          
          // Attendre un court délai pour s'assurer que le réseau est stable
          await new Promise(resolve => setTimeout(resolve, 2000));
          
          let successCount = 0;
          for (const img of remainingShopImages) {
            try {
              // S'assurer que l'image a le bon type
              if (img.type !== 'shop_delivery') {
                img.type = 'shop_delivery';
                await dataService.updatePendingImage(img.id, img);
              }
              
              // Tentative de synchronisation avec diagnostic détaillé
              console.log(`Tentative supplémentaire pour l'image shop ${img.id}:`, {
                deliveryId: img.deliveryId,
                context: img.deliveryContext || img.context,
                hasFile: !!img.file,
                hasBase64: !!img.base64Data,
                timestamp: new Date().toISOString()
              });
              
              const serverId = await this.syncShopDeliveryImage(img);
              if (serverId) {
                successCount++;
                console.log(`Image shop ${img.id} synchronisée avec succès lors de la tentative supplémentaire (serverId: ${serverId})`);
              }
            } catch (imgError) {
              console.warn(`Échec de la tentative supplémentaire pour l'image shop ${img.id}:`, imgError);
              
              // Enregistrer les informations d'erreur
              logService.syncError(`Échec de synchronisation de l'image shop ${img.id} lors de la tentative supplémentaire`, {
                error: imgError.message,
                stack: imgError.stack,
                status: imgError.response?.status,
                imageData: {
                  id: img.id,
                  type: img.type,
                  deliveryContext: img.deliveryContext || img.context,
                  hasFile: !!img.file,
                  hasBase64: !!img.base64Data
                }
              });
            }
          }
          
          if (successCount > 0) {
            console.log(`${successCount}/${remainingShopImages.length} images shop_delivery synchronisées avec succès lors de la tentative supplémentaire`);
          }
        }
      } catch (checkError) {
        console.warn('Erreur lors de la vérification finale des images shop:', checkError);
      }
      
      // Si la synchronisation a réussi, déclencher un événement de rafraîchissement
      if (result.success && result.stats && result.stats.success > 0) {
        // Créer un événement global pour indiquer que les données ont été synchronisées
        const event = new CustomEvent('sync-completed', {
          detail: {
            timestamp: Date.now(),
            stats: result.stats
          }
        });
        
        // Déclencher l'événement sur window et document
        window.dispatchEvent(event);
        document.dispatchEvent(event);
        
        logService.syncInfo('Événement sync-completed émis après synchronisation forcée', {
          stats: result.stats
        });
      }
      
      // Forcer un nettoyage des images pour éviter la confusion lors de la prochaine connexion
      await this.cleanupSyncedImages();
      
      return result;
    } catch (error) {
      logService.syncError('Erreur lors de la synchronisation forcée', {
        error: error.message
      });
      throw error;
    }
  }
  
  // Récupérer le temps écoulé depuis la dernière synchronisation
  getTimeSinceLastSync() {
    if (!this.lastSyncTime) return null;
    return Date.now() - this.lastSyncTime;
  }

  /**
   * Méthode pour mettre en file d'attente une mise à jour (utilise par les composants)
   * @param {string} type - Type de mise à jour ('delivery' ou 'shop_delivery')
   * @param {string} deliveryId - ID de la livraison
   * @param {Object} updates - Données de la mise à jour
   * @returns {Promise<string>} - ID de la mise à jour
   */
  async queueUpdate(type, deliveryId, updates) {
    try {
      if (!deliveryId) throw new Error('ID de livraison requis');
      if (!updates) throw new Error('Données de mise à jour requises');
      
      // Identifier le type de livraison
      const isShopDelivery = type === 'shop_delivery';
      
      // Générer un ID unique pour la mise à jour
      const updateId = `update_${type}_${Date.now()}_${Math.random().toString(36).substring(2, 10)}`;
      
      // Assembler les données de la mise à jour
      const updateData = {
        id: updateId,
        type: type,
        deliveryId: String(deliveryId),
        updates: updates,
        status: updates.status || 'unknown',
        timestamp: new Date().toISOString(),
        synced: false,
        syncAttempts: 0,
        createdAt: new Date().toISOString()
      };
      
      // Si des images sont liées à cette mise à jour
      let imageIds = [];
      
      // Vérifier si la mise à jour contient déjà des imageIds explicites
      if (updates.imageIds && Array.isArray(updates.imageIds) && updates.imageIds.length > 0) {
        console.log(`ImageIds explicites détectés dans queueUpdate type=${type}:`, {
          imageIdsCount: updates.imageIds.length,
          imageIds: updates.imageIds
        });
        imageIds = [...updates.imageIds];
      }
      
      // Si une photo est fournie et pas déjà dans imageIds
      if (updates.photo && !imageIds.includes(updates.photo)) {
        console.log(`Photo détectée dans queueUpdate type=${type}:`, {
          photoType: typeof updates.photo,
          isId: updates.photo && typeof updates.photo === 'string' && 
                (updates.photo.startsWith('shop_img_') || updates.photo.startsWith('img_'))
        });
        
        // Si la photo est un ID d'image déjà stockée (commence généralement par 'img_' ou 'shop_img_')
        if (typeof updates.photo === 'string' && 
            (updates.photo.startsWith('img_') || updates.photo.startsWith('shop_img_'))) {
          // C'est déjà un ID d'image, l'utiliser directement
          imageIds.push(updates.photo);
          console.log(`Utilisation de l'ID d'image existant: ${updates.photo}`);
        } else {
          // Sinon, c'est probablement une valeur base64 ou autre chose qu'il faut stocker
          // Mais cela ne devrait plus arriver avec les changements apportés
          console.warn('La photo fournie n\'est pas un ID d\'image. Type:', typeof updates.photo);
        }
      }
      
      // Ajouter les IDs d'image à la mise à jour (s'il y en a)
      if (imageIds.length > 0) {
        updateData.imageIds = imageIds;
        console.log(`${imageIds.length} imageIds ajoutés à la mise à jour ${updateId}:`, imageIds);
      }
      
      // Si la mise à jour concerne une livraison non effectuée ('not_delivered' ou 'NOT DELIVERED')
      if (updates.undone_reason) {
        // Utiliser undoneReason (camelCase) uniformément pour toutes les opérations internes
        updateData.undoneReason = updates.undone_reason;
        
        // Log pour faciliter le débogage des problèmes de raison
        logService.syncInfo(`Raison de non-livraison enregistrée: ${updates.undone_reason}`, {
          deliveryId,
          type,
          updateId
        });
      }
      
      // Enregistrer la mise à jour dans la file d'attente
      await dataService.storePendingUpdate(updateData);
      
      // Également mettre à jour la copie locale de la livraison si disponible
      if (isShopDelivery) {
        try {
          const cachedDelivery = await dataService.getShopDelivery(deliveryId);
          if (cachedDelivery) {
            const updatedDelivery = { ...cachedDelivery, ...updates };
            await dataService.saveShopDelivery(deliveryId, updatedDelivery);
          }
        } catch (cacheError) {
          logService.error(`Erreur lors de la mise à jour du cache de la livraison shop ${deliveryId}`, {
            error: cacheError.message,
            updates
          });
          // Continuer malgré l'erreur
        }
      } else {
        try {
          const cachedDelivery = await dataService.getDelivery(deliveryId);
          if (cachedDelivery) {
            const updatedDelivery = { ...cachedDelivery, ...updates };
            await dataService.saveDelivery(deliveryId, updatedDelivery);
          }
        } catch (cacheError) {
          logService.error(`Erreur lors de la mise à jour du cache de la livraison ${deliveryId}`, {
            error: cacheError.message,
            updates
          });
          // Continuer malgré l'erreur
        }
      }
      
      // Essayer de synchroniser immédiatement si en ligne
      if (navigator.onLine) {
        this.scheduleUpdateRetry(updateId, updateData);
      }
      
      return updateId;
    } catch (error) {
      logService.error('Erreur lors de la mise en file d\'attente de la mise à jour', {
        type,
        deliveryId,
        error: error.message
      });
      throw error;
    }
  }

  /**
   * Méthode pour synchroniser une image de shop_delivery
   * @param {Object} imageData - Données de l'image à synchroniser
   * @returns {Promise<string|null>} - ID de l'image côté serveur ou null
   */
  async syncShopDeliveryImage(imageData) {
    try {
      // Vérifier si cette image est déjà en cours de synchronisation par une autre instance
      if (this.#inProgressSyncs.has(imageData.id)) {
        console.log(`Synchronisation de l'image ${imageData.id} ignorée - déjà en cours par une autre instance`);
        
        // Attendre un peu puis vérifier si l'image a été synchronisée entre-temps
        await new Promise(resolve => setTimeout(resolve, 2000));
        try {
          const freshImageData = await dataService.getPendingImage(imageData.id);
          if (freshImageData && freshImageData.synced && freshImageData.serverImageId) {
            console.log(`Image ${imageData.id} synchronisée par une autre instance, serverId: ${freshImageData.serverImageId}`);
            return freshImageData.serverImageId;
          }
        } catch (e) {
          // Ignorer l'erreur
        }
        
        return null;
      }
      
      // Marquer cette image comme étant en cours de synchronisation
      this.#inProgressSyncs.add(imageData.id);
      
      console.log(`Début de syncShopDeliveryImage pour l'image ${imageData.id}`, {
        dataType: typeof imageData,
        hasFile: !!imageData.file,
        fileType: imageData.file ? (typeof imageData.file) : 'aucun',
        deliveryId: imageData.deliveryId,
        context: imageData.deliveryContext
      });
    
      // Vérifier IMMÉDIATEMENT si cette image n'a pas été synchronisée entre-temps
      try {
        const freshImageData = await dataService.getPendingImage(imageData.id);
        if (freshImageData && freshImageData.synced && freshImageData.serverImageId) {
          console.log(`Image ${imageData.id} déjà synchronisée entre-temps, serverId: ${freshImageData.serverImageId}`);
          return freshImageData.serverImageId;
        }
      } catch (checkError) {
        // Ignorer l'erreur et continuer
        console.warn(`Erreur lors de la vérification préalable de l'image ${imageData.id}:`, checkError);
      }
    
      // Si l'image a déjà été synchronisée, ne rien faire
      if (imageData.synced && imageData.serverImageId) {
        console.log(`Image shop ${imageData.id} déjà synchronisée, serverId: ${imageData.serverImageId}`);
        return imageData.serverImageId;
      }
      
      // Génération d'un ID de synchronisation unique pour cette tentative
      const syncAttemptId = `${Date.now()}_${Math.random().toString(36).substring(2, 10)}`;
      
      // Mécanisme de verrouillage pour éviter les synchronisations simultanées
      const lockKey = `sync_lock_image_${imageData.id}`;
      const currentLock = localStorage.getItem(lockKey);
      
      // Si un verrou existe et date de moins de 60 secondes, annuler
      if (currentLock) {
        // Vérifier si c'est un verrou récent
        const lockParts = currentLock.split('|');
        const lockTime = parseInt(lockParts[0], 10);
        const lockOwner = lockParts.length > 1 ? lockParts[1] : 'unknown';
        const now = Date.now();
        const lockAge = now - lockTime;
        
        // Si le verrou date de moins de 60 secondes (les images peuvent prendre plus de temps que les mises à jour)
        if (lockAge < 60000) {
          console.log(`Synchronisation de l'image ${imageData.id} ignorée - déjà en cours (verrou posé il y a ${Math.floor(lockAge/1000)}s par ${lockOwner})`);
          
          // Si on a déjà un serverImageId, le retourner quand même
          if (imageData.serverImageId) {
            return imageData.serverImageId;
          }
          
          // Vérifier encore une fois si l'image a été synchronisée entre-temps
          try {
            const freshImageData = await dataService.getPendingImage(imageData.id);
            if (freshImageData && freshImageData.synced && freshImageData.serverImageId) {
              console.log(`Image ${imageData.id} synchronisée pendant l'attente, serverId: ${freshImageData.serverImageId}`);
              return freshImageData.serverImageId;
            }
          } catch (checkError) {
            // Ignorer l'erreur et continuer
          }
          
          return null;
        } else {
          // Verrou trop ancien, on le supprime
          console.log(`Verrou périmé pour l'image ${imageData.id} (${Math.floor(lockAge/1000)}s, posé par ${lockOwner}), libération`);
          localStorage.removeItem(lockKey);
        }
      }
      
      // Poser un verrou avec l'ID de tentative unique
      localStorage.setItem(lockKey, `${Date.now()}|${syncAttemptId}`);
      console.log(`Verrou posé pour l'image ${imageData.id} par ${syncAttemptId}`);
      
      // S'assurer que le type est correct et cohérent
      const correctedImageData = { ...imageData };
      if (!correctedImageData.type || correctedImageData.type !== 'shop_delivery') {
        correctedImageData.type = 'shop_delivery';
      }
      
      try {
        // S'assurer que le contexte est défini
        if (!correctedImageData.deliveryContext) {
          if (correctedImageData.context) {
            correctedImageData.deliveryContext = correctedImageData.context;
          } else {
            correctedImageData.deliveryContext = 'shop_photo';
          }
        }
        
        // Récupérer l'email pour les logs
        const userEmail = localStorage.getItem('email') || 'email_non_disponible';
        
        // Si l'appareil n'est pas en ligne, impossible de synchroniser
        if (!navigator.onLine) {
          logService.syncWarning(`Tentative de synchronisation de l'image shop ${correctedImageData.id} impossible car hors ligne`, {
            shopDeliveryId: correctedImageData.deliveryId,
            userEmail,
            timestamp: new Date().toISOString()
          });
          
          // Au lieu de simplement retourner null, mettre l'image en file d'attente 
          // pour synchronisation ultérieure quand l'appareil sera en ligne
          try {
            // Vérifier si l'image est déjà marquée pour synchro
            const pendingImages = await dataService.getPendingImages();
            const existingImage = pendingImages.find(img => img.id === correctedImageData.id);
            
            if (!existingImage) {
              // Sauvegarder l'image dans le stockage local si elle n'existe pas déjà
              console.log(`Mise en file d'attente de l'image shop ${correctedImageData.id} pour synchronisation ultérieure`);
              
              // S'assurer que l'image n'est pas marquée comme synchronisée
              correctedImageData.synced = false;
              correctedImageData.needsSync = true;
              correctedImageData.queuedAt = new Date().toISOString();
              
              // Stocker l'image pour synchronisation ultérieure
              await dataService.storePendingImage(correctedImageData.id, correctedImageData);
              
              logService.syncInfo(`Image shop ${correctedImageData.id} mise en file d'attente pour synchronisation ultérieure`, {
                deliveryId: correctedImageData.deliveryId,
                type: 'shop_delivery',
                context: correctedImageData.deliveryContext || 'shop_photo'
              });
            } else {
              console.log(`Image shop ${correctedImageData.id} déjà en file d'attente`);
            }
            
            // Bien que nous ne puissions pas synchroniser maintenant, retourner l'ID local
            // pour que le processus de mise à jour puisse continuer
            return correctedImageData.id;
          } catch (storageError) {
            console.error(`Erreur lors de la mise en file d'attente de l'image shop ${correctedImageData.id}:`, storageError);
            logService.syncError(`Erreur lors de la mise en file d'attente de l'image shop`, {
              imageId: correctedImageData.id,
              deliveryId: correctedImageData.deliveryId,
              error: storageError.message
            });
          }
          
          return null;
        }
        
        // Vérifier encore une fois si cette image n'a pas été synchronisée par une autre tâche parallèle
        const freshImageData = await dataService.getPendingImage(correctedImageData.id);
        if (freshImageData && freshImageData.synced && freshImageData.serverImageId) {
          console.log(`Image ${correctedImageData.id} déjà synchronisée par une autre tâche, serverId: ${freshImageData.serverImageId}`);
          return freshImageData.serverImageId;
        }
        
        // Continuer avec le reste du code de synchronisation d'image...
        // Préparer l'objet File pour l'envoi
        let fileObject = null;
        
        // Vérifier si on a déjà un objet File valide
        if (correctedImageData.file && typeof correctedImageData.file === 'object' && correctedImageData.file instanceof File) {
          fileObject = correctedImageData.file;
          console.log(`Objet File existant utilisé pour l'image shop ${correctedImageData.id}`);
        } 
        // Sinon, essayer de récupérer l'objet File depuis le cache
        else if (dataService.getFileObjectFromCache && correctedImageData.id) {
          fileObject = await dataService.getFileObjectFromCache(correctedImageData.id);
          console.log(`Objet File récupéré depuis le cache pour l'image shop ${correctedImageData.id}:`, !!fileObject);
        }
        
        // Si on n'a toujours pas d'objet File, essayer de le reconstruire à partir des données
        if (!fileObject && correctedImageData.base64Data) {
          try {
            const byteString = atob(correctedImageData.base64Data.split(',')[1]);
            const mimeString = correctedImageData.base64Data.split(',')[0].split(':')[1].split(';')[0];
            const arrayBuffer = new ArrayBuffer(byteString.length);
            const intArray = new Uint8Array(arrayBuffer);
            
            for (let i = 0; i < byteString.length; i++) {
              intArray[i] = byteString.charCodeAt(i);
            }
            
            const blob = new Blob([arrayBuffer], { type: mimeString });
            fileObject = new File([blob], correctedImageData.fileName || `shop_image_${Date.now()}.jpg`, {
              type: mimeString,
              lastModified: Date.now()
            });
            
            console.log(`Objet File reconstruit à partir des données base64 pour l'image shop ${correctedImageData.id}`);
          } catch (base64Error) {
            console.error(`Erreur lors de la reconstruction du File à partir des données base64 pour ${correctedImageData.id}:`, base64Error);
          }
        }
        
        // Vérifier si on a un objet File valide
        if (!fileObject) {
          throw new Error(`Impossible de récupérer ou reconstruire l'objet File pour l'image shop ${correctedImageData.id}`);
        }
        
        // Créer le FormData pour l'envoi
        const formData = new FormData();
        formData.append('image', fileObject);
        formData.append('shop_delivery_id', correctedImageData.deliveryId);
        formData.append('context', correctedImageData.deliveryContext || 'shop_photo');
        
        // Ajouter d'autres métadonnées utiles
        if (correctedImageData.lat) formData.append('lat', correctedImageData.lat);
        if (correctedImageData.lng) formData.append('lng', correctedImageData.lng);
        if (correctedImageData.timestamp) formData.append('timestamp', correctedImageData.timestamp);
        
        // Log détaillé pour diagnostic
        console.log(`FormData préparé pour l'image shop ${correctedImageData.id}:`, {
          hasFileObject: !!fileObject,
          fileName: fileObject.name,
          fileType: fileObject.type,
          fileSize: fileObject.size,
          deliveryId: correctedImageData.deliveryId,
          context: correctedImageData.deliveryContext || 'shop_photo'
        });

        
        // Configurer la requête avec un timeout adapté
        const config = {
          timeout: 60000, // 60 secondes
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        };
        
        console.log(`Envoi de la requête POST pour l'image shop ${correctedImageData.id} vers /livraison/shopDeliveryImage/`);
        
        // Envoyer l'image au serveur avec le bon endpoint pour shop deliveries
        const response = await axios.post('/livraison/shopDeliveryImage/', formData, config);
        
        // Récupérer l'ID de l'image côté serveur
        const serverId = response.data.id;
        console.log(`Image shop ${correctedImageData.id} uploadée avec succès. ServerId: ${serverId}`);
        
        // Marquer l'image comme synchronisée dans le stockage local
        const updatedImageData = { ...correctedImageData };
        updatedImageData.synced = true;
        updatedImageData.serverImageId = serverId;
        updatedImageData.syncedAt = new Date().toISOString();
        
        // Mettre à jour les données de l'image dans le stockage
        try {
          await dataService.updatePendingImage(correctedImageData.id, updatedImageData);
          console.log(`Données d'image shop ${correctedImageData.id} mises à jour avec succès dans le stockage local`);
        } catch (storageError) {
          console.warn(`Impossible de mettre à jour le statut de l'image shop ${correctedImageData.id} après synchronisation:`, storageError);
          logService.syncWarning(`Impossible de mettre à jour le statut de l'image shop ${correctedImageData.id} après synchronisation`, {
            error: storageError.message,
            deliveryId: correctedImageData.deliveryId
          });
        }
        
        logService.syncInfo(`Image shop ${correctedImageData.id} synchronisée avec succès (server ID: ${serverId})`, {
          deliveryId: correctedImageData.deliveryId,
          userEmail,
          serverId,
          timestamp: new Date().toISOString()
        });
        
        return serverId;
      } finally {
        // Toujours libérer le verrou, quoi qu'il arrive
        const currentLockValue = localStorage.getItem(lockKey);
        if (currentLockValue && currentLockValue.includes(`|${syncAttemptId}`)) {
          // Ne libérer que si c'est notre verrou
          localStorage.removeItem(lockKey);
          console.log(`Verrou libéré pour l'image ${imageData.id} par ${syncAttemptId}`);
        } else if (currentLockValue) {
          // Ne pas toucher au verrou d'une autre tentative
          console.log(`Verrou non libéré pour l'image ${imageData.id} - appartient à une autre tentative`);
        }
        
        // Retirer l'image de la liste des synchronisations en cours
        this.#inProgressSyncs.delete(imageData.id);
      }
    } catch (error) {
      console.error(`Exception dans syncShopDeliveryImage pour ${correctedImageData?.id}:`, error);
      
      // Récupérer l'email même en cas d'erreur
      const userEmail = localStorage.getItem('email') || 'email_non_disponible';
      
      // Extraire les métadonnées de l'image pour les logs
      const imageMetadata = {
        id: correctedImageData?.id,
        fileName: correctedImageData?.fileName,
        fileType: correctedImageData?.fileType,
        size: correctedImageData?.size || (correctedImageData?.file ? (typeof correctedImageData.file.size === 'number' ? correctedImageData.file.size : 'non disponible') : 'non disponible'),
        deliveryId: correctedImageData?.deliveryId,
        deliveryContext: correctedImageData?.deliveryContext,
        timestamp: correctedImageData?.timestamp,
        syncAttempts: correctedImageData?.syncAttempts || 0
      };
      
      logService.syncError(`Erreur lors de la synchronisation de l'image shop ${correctedImageData?.id}`, {
        deliveryId: correctedImageData?.deliveryId,
        error: error.message,
        stack: error.stack,
        status: error.response?.status,
        statusText: error.response?.statusText,
        responseData: error.response?.data,
        userEmail,
        imageMetadata,
        timestamp: new Date().toISOString()
      });
      
      // Marquer l'image avec une tentative échouée pour réessayer plus tard
      try {
        const updatedImageData = { ...correctedImageData };
        updatedImageData.syncAttempts = (correctedImageData.syncAttempts || 0) + 1;
        updatedImageData.lastSyncAttempt = new Date().toISOString();
        updatedImageData.lastError = {
          message: error.message,
          status: error.response?.status,
          statusText: error.response?.statusText,
          time: new Date().toISOString(),
          type: error.name || 'UnknownError',
          isNetworkError: !navigator.onLine || error.message.includes('Network Error')
        };
        
        // Planifier une nouvelle tentative avec backoff exponentiel
        this.scheduleImageRetry(correctedImageData.id, updatedImageData);
        
        // Mettre à jour les données de l'image dans le stockage
        await dataService.updatePendingImage(updatedImageData.id, updatedImageData);
      } catch (storageError) {
        console.error(`Impossible de mettre à jour les informations de tentative pour l'image shop ${correctedImageData?.id}:`, storageError);
        logService.syncError(`Impossible de mettre à jour les informations de tentative pour l'image shop ${correctedImageData?.id}`, {
          error: storageError.message,
          deliveryId: correctedImageData?.deliveryId,
          originalError: error.message
        });
      }
      
      return null;
    }
  }

  /**
   * Méthode pour synchroniser une mise à jour de shop_delivery
   * @param {Object} updateData - Données de la mise à jour
   * @returns {Promise<Object>} - Résultat de la synchronisation
   */
  async syncShopDeliveryUpdate(updateData) {
    try {
      // Vérifier que les données sont valides
      if (!updateData || !updateData.deliveryId) {
        throw new Error("Données de mise à jour invalides");
      }

      // Mécanisme de verrouillage pour éviter les synchronisations simultanées
      const lockKey = `sync_lock_shop_${updateData.deliveryId}`;
      const currentLock = localStorage.getItem(lockKey);
      
      // Si un verrou existe et date de moins de 30 secondes, annuler
      if (currentLock) {
        const lockTime = parseInt(currentLock, 10);
        const now = Date.now();
        const lockAge = now - lockTime;
        
        // Si le verrou date de moins de 30 secondes
        if (lockAge < 30000) {
          console.log(`Synchronisation de shop_delivery ${updateData.deliveryId} ignorée - déjà en cours (verrou posé il y a ${Math.floor(lockAge/1000)}s)`);
          return { success: true, skipped: true, reason: 'already_in_progress' };
        } else {
          // Verrou trop ancien, on le supprime
          console.log(`Verrou périmé pour shop_delivery ${updateData.deliveryId} (${Math.floor(lockAge/1000)}s), libération`);
          localStorage.removeItem(lockKey);
        }
      }
      
      // Poser un verrou
      localStorage.setItem(lockKey, Date.now().toString());
      console.log(`Verrou posé pour shop_delivery ${updateData.deliveryId}`);

      // Récupérer l'email de l'utilisateur depuis le localStorage
      const userEmail = localStorage.getItem('email') || 'email_non_disponible';

      // Arrays pour stocker les IDs d'images
      const serverImageIds = [];
      const processedLocalImageIds = [];
      const failedImageIds = [];
      const imageErrorDetails = [];

      try {
        // Synchroniser les images d'abord, de façon indépendante
        if (updateData.imageIds?.length) {
          logService.syncInfo(`Synchronisation de ${updateData.imageIds.length} images pour la mise à jour shop ${updateData.id}`, {
            deliveryId: updateData.deliveryId,
            userEmail,
            imageIds: updateData.imageIds,
            updateContext: updateData.context || 'unknown_context',
            updateStatus: updateData.status
          });
          
          // Synchroniser les images une par une
          for (const imageId of updateData.imageIds) {
            // Vérifier si cette image a déjà été traitée
            if (processedLocalImageIds.includes(imageId)) continue;
            
            processedLocalImageIds.push(imageId);
            
            // Récupérer les données de l'image
            const imageData = await dataService.getPendingImage(imageId);
            
            if (!imageData) {
              logService.syncWarning(`Image ${imageId} non trouvée dans le stockage local pour la mise à jour shop ${updateData.id}`, {
                deliveryId: updateData.deliveryId,
                userEmail
              });
              continue;
            }

            // Vérifier si l'image a déjà un ID serveur
            if (imageData.serverImageId) {
              serverImageIds.push(imageData.serverImageId);
              
              // Marquer l'image comme synchronisée et la supprimer
              imageData.synced = true;
              await dataService.updatePendingImage(imageId, imageData);
              await dataService.removePendingImage(imageId);
              
              console.log(`Image ${imageId} avec serverId ${imageData.serverImageId} marquée comme synchronisée et supprimée`);
              continue;
            }
            
            // Si l'image n'est pas encore synchronisée, essayer de la synchroniser
            if (!imageData.synced) {
              try {
                const serverId = await this.syncShopDeliveryImage(imageData);
                if (serverId) {
                  serverImageIds.push(serverId);
                  
                  // Marquer l'image comme synchronisée et la supprimer
                  imageData.synced = true;
                  imageData.serverImageId = serverId;
                  await dataService.updatePendingImage(imageId, imageData);
                  await dataService.removePendingImage(imageId);
                  
                  console.log(`Image ${imageId} synchronisée avec succès (serverId: ${serverId}) et supprimée`);
                }
              } catch (imageError) {
                failedImageIds.push(imageId);
                
                // Collecter les détails de l'erreur pour le rapport final
                const errorDetail = {
                  imageId,
                  fileName: imageData.fileName,
                  fileType: imageData.fileType,
                  fileSize: imageData.size,
                  errorMessage: imageError.message,
                  errorCode: imageError.response?.status,
                  errorType: imageError.name || 'UnknownError',
                  isNetworkError: !navigator.onLine || imageError.message.includes('Network Error'),
                  context: imageData.deliveryContext,
                  timestamp: new Date().toISOString()
                };
                
                imageErrorDetails.push(errorDetail);
                
                // Continuer avec les autres images et la mise à jour même si celle-ci échoue
              }
            }
          }

          // Attendre un court délai pour s'assurer que le serveur a traité les images
          if (serverImageIds.length > 0) {
            await new Promise(resolve => setTimeout(resolve, 1000)); // 1 seconde d'attente
          }
        }

        // TOUJOURS tenter de mettre à jour le statut de la livraison, même si les images ont échoué
        if (navigator.onLine) {
          const updatePayload = {
            status: updateData.status,
            undone_reason: updateData.undoneReason
          };

          // Si des images ont été synchronisées, les inclure dans la mise à jour
          if (serverImageIds.length > 0) {
            console.log(`Inclusion des images (${serverImageIds.length}) dans la mise à jour shop_delivery:`, {
              deliveryId: updateData.deliveryId,
              status: updateData.status,
              serverImageIds
            });
            
            // if (updateData.status === 'not_delivered') {
            //   updatePayload.photo_anomalie = serverImageIds[0]; // Pour les signalements de problème
            // } else if (updateData.status === 'delivered') {
            //   updatePayload.photo = serverImageIds[0]; // Pour les confirmations de livraison
            // }
          }

          logService.syncInfo(`Envoi de la mise à jour shop ${updateData.id} pour la livraison ${updateData.deliveryId}`, {
            deliveryId: updateData.deliveryId,
            status: updateData.status,
            imageCount: serverImageIds.length,
            payload: updatePayload,
            userEmail,
            failedImagesCount: failedImageIds.length,
            timestamp: new Date().toISOString()
          });

          // Utiliser l'endpoint approprié pour les shop deliveries
          const response = await axios.patch(`/livraison/shopDelivery/${updateData.deliveryId}/`, updatePayload);

          // Récupérer la réponse du serveur
          const responseData = response.data;

          // Mise à jour explicite dans le cache pour une actualisation visuelle immédiate
          try {
            // Ici on s'assure que le cache local est mis à jour immédiatement, même en mode online
            const cachedDelivery = await dataService.getShopDelivery(updateData.deliveryId);
            if (cachedDelivery) {
              const updatedCachedDelivery = { 
                ...cachedDelivery,
                status: updateData.status // Important: mise à jour du statut
              };
              
              // Si on utilise également les données côté serveur, les fusionner
              if (responseData) {
                Object.assign(updatedCachedDelivery, responseData);
              }
              
              // Sauvegarder dans le cache local
              await dataService.saveShopDelivery(updateData.deliveryId, updatedCachedDelivery);
              console.log(`Cache local mis à jour pour shop_delivery ${updateData.deliveryId} avec statut ${updateData.status}`);
            }
          } catch (cacheUpdateError) {
            console.warn(`Erreur lors de la mise à jour du cache pour shop_delivery ${updateData.deliveryId}:`, cacheUpdateError);
          }

          // Marquer la mise à jour comme synchronisée
          updateData.synced = true;
          updateData.syncedAt = new Date().toISOString();
          updateData.serverResponse = {
            status: response.status,
            data: responseData
          };

          // Mettre à jour l'état stocké
          await dataService.storePendingUpdate(updateData);

          // Nettoyer toutes les mises à jour pour cette livraison
          try {
            const allUpdates = await dataService.getPendingUpdates();
            const relatedUpdates = allUpdates.filter(update => 
              update.deliveryId === updateData.deliveryId && 
              !update.synced && 
              update.id !== updateData.id
            );
            
            if (relatedUpdates.length > 0) {
              console.log(`Nettoyage de ${relatedUpdates.length} autres mises à jour pour la livraison shop ${updateData.deliveryId}`);
              
              for (const update of relatedUpdates) {
                update.synced = true;
                update.syncedAt = new Date().toISOString();
                update.syncedBy = updateData.id;
                await dataService.storePendingUpdate(update);
                await dataService.removePendingUpdate(update.id);
              }
            }
          } catch (cleanupError) {
            console.warn(`Erreur lors du nettoyage des mises à jour liées à ${updateData.deliveryId}:`, cleanupError);
          }
          
          // Nettoyer toutes les images associées à cette livraison
          try {
            const allImages = await dataService.getPendingImages();
            const relatedImages = allImages.filter(img => img.deliveryId === updateData.deliveryId);
            
            if (relatedImages.length > 0) {
              console.log(`Nettoyage de ${relatedImages.length} images pour la livraison shop ${updateData.deliveryId}`);
              
              for (const img of relatedImages) {
                // Si l'image a un ID serveur, la marquer comme synchronisée
                if (img.serverImageId) {
                  img.synced = true;
                  await dataService.updatePendingImage(img.id, img);
                }
                
                // Supprimer l'image dans tous les cas
                await dataService.removePendingImage(img.id);
              }
            }
          } catch (imgCleanupError) {
            console.warn(`Erreur lors du nettoyage des images liées à ${updateData.deliveryId}:`, imgCleanupError);
          }

          // Mettre à jour le cache de la livraison shop si disponible
          if (responseData) {
            await dataService.saveShopDelivery(updateData.deliveryId, responseData);
          }

          // Log du succès
          logService.syncInfo(`Mise à jour shop ${updateData.id} pour la livraison ${updateData.deliveryId} synchronisée avec succès`, {
            deliveryId: updateData.deliveryId,
            status: updateData.status,
            imageCount: serverImageIds.length,
            successfulImages: serverImageIds.length,
            failedImages: failedImageIds.length,
            response: {
              status: response.status,
              data: responseData
            },
            userEmail
          });

          // INTÉGRATION CRITIQUE AVEC TOURNEE
          // En mode online, la modification n'apparaît pas dans MyTournee car elle ne partage
          // pas le même cache que les shop_deliveries - nous devons mettre à jour manuellement
          // la livraison dans la tournée courante pour garantir la visibilité instantanée
          try {
            console.log(`Mise à jour forcée de la livraison ${updateData.deliveryId} dans la tournée courante avec statut=${updateData.status}`);
            // Mettre à jour directement via la méthode spécifique qui modifie la tournée
            await this.updateDeliveryInCurrentTournee(updateData.deliveryId, {
              status: updateData.status,
              // Autres données pertinentes pour la mise à jour visuelle
              lastUpdate: new Date().toISOString()
            });
            
            // Si nous avons modifié une shop_delivery dans la tournée, il faut forcer un rafraîchissement
            // global pour que tous les composants soient avertis
            const refreshEvent = new CustomEvent('app-data-refresh', {
              detail: {
                source: 'shop_delivery_update',
                deliveryId: updateData.deliveryId,
                status: updateData.status,
                timestamp: Date.now()
              }
            });
            window.dispatchEvent(refreshEvent);
            document.dispatchEvent(refreshEvent);
            console.log(`Événement app-data-refresh global émis pour forcer le rafraîchissement de tous les composants`);
            
          } catch (tourneeUpdateError) {
            console.warn(`Erreur lors de la mise à jour forcée dans la tournée:`, tourneeUpdateError);
            // Continuer même en cas d'erreur, ne pas bloquer le flux
          }
          
          // NOUVELLE APPROCHE RADICALE POUR RÉSOUDRE LE PROBLÈME DE MISE À JOUR VISUELLE
          // 1. Émettre des événements standards (compatibilité rétroactive)
          this.emitDeliveryUpdatedEvent(updateData.deliveryId, updateData.status);
          this.emitDeliveryUpdatedEvent(`shop_${updateData.deliveryId}`, updateData.status);
          
          // 2. Événement spécifique pour shop deliveries
          try {
            const shopEvent = new CustomEvent('shop-delivery-updated', {
              detail: {
                deliveryId: updateData.deliveryId,
                status: updateData.status,
                timestamp: Date.now()
              }
            });
            window.dispatchEvent(shopEvent);
            document.dispatchEvent(shopEvent);
          } catch (eventError) {
            console.warn(`Erreur lors de l'émission de l'événement shop-delivery-updated:`, eventError);
          }
          
          // 3. SOLUTION FORCÉE: Forcer une actualisation complète de l'application
          try {
            // Mettre à jour le statut dans la tournée actuelle (approche directe)
            const currentTournee = JSON.parse(localStorage.getItem('current_tournee') || '{}');
            
            // DEUX MÉTHODES DE RECHERCHE DISTINCTES POUR SHOP_DELIVERIES ET DELIVERIES STANDARD
            if (currentTournee && currentTournee.deliveries) {
              let found = false;
              // Recherche normale pour les IDs numériques standard
              for (let i = 0; i < currentTournee.deliveries.length; i++) {
                const delivery = currentTournee.deliveries[i];
                
                // MÉTHODE #1: Comparaison directe des IDs
                                 if (delivery.id == updateData.deliveryId) {
                   console.log(`Livraison trouvée par ID direct dans tournée: ${updateData.deliveryId}`);
                   
                   // CORRECTION CRITIQUE: Normalisation de la casse du statut en fonction du type
                   // Les shop_deliveries utilisent des statuts en minuscules, les deliveries standard en majuscules
                   const isShopDelivery = updateData.type === 'shop_delivery' || 
                                         (delivery.shop_delivery_id !== undefined) || 
                                         (delivery.metadata && delivery.metadata.shop_delivery_id);
                                         
                   // Appliquer le statut avec la bonne casse selon le type
                   if (isShopDelivery) {
                     // Pour shop_deliveries: statut en minuscules
                     delivery.status = updateData.status.toLowerCase();
                     console.log(`Statut appliqué en minuscules (shop_delivery): "${delivery.status}"`);
                   } else {
                     // Pour deliveries standard: statut en majuscules
                     delivery.status = updateData.status.toUpperCase();
                     console.log(`Statut appliqué en majuscules (delivery standard): "${delivery.status}"`);
                   }
                   
                   delivery.lastUpdate = new Date().toISOString();
                   found = true;
                   break;
                 }
                 
                 // MÉTHODE #2: Recherche par shop_delivery_id dans les propriétés
                 if (delivery.shop_delivery_id && delivery.shop_delivery_id == updateData.deliveryId) {
                   console.log(`Livraison trouvée par shop_delivery_id dans tournée: ${updateData.deliveryId}`);
                   
                   // Pour les shop_deliveries: statut en minuscules
                   delivery.status = updateData.status.toLowerCase();
                   console.log(`Statut appliqué en minuscules (shop_delivery): "${delivery.status}"`);
                   
                   delivery.lastUpdate = new Date().toISOString();
                   found = true;
                   break;
                 }
                 
                 // MÉTHODE #3: Recherche par métadonnées
                 if (delivery.metadata && delivery.metadata.shop_delivery_id == updateData.deliveryId) {
                   console.log(`Livraison trouvée par metadata.shop_delivery_id dans tournée: ${updateData.deliveryId}`);
                   
                   // Pour les shop_deliveries: statut en minuscules
                   delivery.status = updateData.status.toLowerCase();
                   console.log(`Statut appliqué en minuscules (shop_delivery): "${delivery.status}"`);
                   
                   delivery.lastUpdate = new Date().toISOString();
                   found = true;
                   break;
                }
              }
              
              // Si rien n'a été trouvé, essayer en mode debug pour voir les relations potentielles
              if (!found) {
                console.log(`Recherche manuelle exhaustive pour shop_delivery ${updateData.deliveryId} dans les ${currentTournee.deliveries.length} livraisons de la tournée...`);
                
                // Parcourir et logger chaque livraison pour debug
                for (let i = 0; i < currentTournee.deliveries.length; i++) {
                  const delivery = currentTournee.deliveries[i];
                  if (String(delivery.id).includes(String(updateData.deliveryId)) || 
                      (delivery.metadata && JSON.stringify(delivery.metadata).includes(String(updateData.deliveryId)))) {
                    console.log(`Correspondance partielle trouvée pour ${updateData.deliveryId}:`, {
                      tourneeId: delivery.id,
                      hasShopId: !!delivery.shop_delivery_id,
                      metadata: delivery.metadata
                    });
                    
                    // Application forcée en cas de correspondance partielle
                    // En cas de shop_delivery, on applique le statut en minuscules
                    currentTournee.deliveries[i].status = updateData.status.toLowerCase();
                    console.log(`Correspondance partielle: statut appliqué en minuscules (shop_delivery): "${currentTournee.deliveries[i].status}"`);
                    
                    currentTournee.deliveries[i].lastUpdate = new Date().toISOString();
                    found = true;
                  }
                }
              }
              
              if (found) {
                localStorage.setItem('current_tournee', JSON.stringify(currentTournee));
                console.log(`Mise à jour forcée du statut dans la tournée actuelle pour ${updateData.deliveryId}`);
              } else {
                console.warn(`Impossible de trouver la shop_delivery ${updateData.deliveryId} dans la tournée actuelle pour mise à jour manuelle`);
              }
            }
            
            // MISE À JOUR FORCÉE DU CACHE DE LA LIVRAISON
            try {
              const cachedShopDelivery = await dataService.getShopDelivery(updateData.deliveryId);
              if (cachedShopDelivery) {
                // Pour les shop_deliveries: toujours appliquer le statut en minuscules
                cachedShopDelivery.status = updateData.status.toLowerCase();
                cachedShopDelivery.lastUpdate = new Date().toISOString();
                await dataService.saveShopDelivery(updateData.deliveryId, cachedShopDelivery);
                console.log(`Cache de la shop_delivery ${updateData.deliveryId} mis à jour manuellement avec statut=${cachedShopDelivery.status} (minuscules)`);
              }
            } catch (cacheError) {
              console.warn(`Erreur lors de la mise à jour forcée du cache de shop_delivery:`, cacheError);
            }
            
            // TECHNIQUE NUCLÉAIRE: FORCER LA DÉCONNEXION/RECONNEXION DU CACHE DE TOURNEE
            try {
              // 1. Sauvegarder la tournée actuelle
              const currentTourneeBackup = localStorage.getItem('current_tournee');
              
              // 2. La supprimer (pour forcer un rechargement complet)
              localStorage.removeItem('current_tournee');
              
              // 3. Attendre un bref moment pour que le système détecte l'absence
              setTimeout(() => {
                // 4. Restaurer la tournée avec nos modifications
                if (currentTourneeBackup) {
                  localStorage.setItem('current_tournee', currentTourneeBackup);
                  console.log('Tournée restaurée après suppression forcée pour rafraîchissement');
                  
                  // 5. Déclencher des événements spécifiques de rafraîchissement de tournée
                  window.dispatchEvent(new CustomEvent('tournee-cache-reset'));
                  window.dispatchEvent(new CustomEvent('tournee-updated'));
                  window.dispatchEvent(new CustomEvent('tournee-data-changed'));
                }
              }, 100);
            } catch (resetError) {
              console.error('Erreur lors de la réinitialisation forcée du cache de tournée:', resetError);
            }
            
            // Triple événement de rafraîchissement pour s'assurer que tous les composants se mettent à jour
            ['force-app-refresh', 'shop-delivery-refresh', 'mytournee-refresh'].forEach(eventName => {
              const refreshEvent = new CustomEvent(eventName, {
                detail: {
                  source: 'shop_delivery_update',
                  deliveryId: updateData.deliveryId,
                  status: updateData.status,
                  timestamp: Date.now(),
                  forceRefresh: true
                }
              });
              window.dispatchEvent(refreshEvent);
              document.dispatchEvent(refreshEvent);
              console.log(`Événement ${eventName} émis pour forcer le rafraîchissement`);
            });
            
            // Émettre également des événements spécifiques pour tous les types de composants
            [
              'tournee-updated', 
              'delivery-list-updated',
              'all-deliveries-updated',
              'shop-deliveries-updated',
              'cache-invalidated',
              'online-data-changed'
            ].forEach(eventName => {
              window.dispatchEvent(new CustomEvent(eventName));
              console.log(`Événement ${eventName} émis pour notification globale`);
            });
            
            // Événement avec détail pour le changement de statut spécifique
                         // Événement avec statut normalisé selon le type
             if (updateData.type === 'shop_delivery') {
               // Pour shop_deliveries: statut en minuscules
               window.dispatchEvent(new CustomEvent('delivery-status-changed', { 
                 detail: { 
                   id: updateData.deliveryId, 
                   status: updateData.status.toLowerCase(),
                   originalStatus: updateData.status,
                   type: 'shop_delivery',
                   forceRefresh: true
                 } 
               }));
               console.log(`Événement de statut émis avec statut en minuscules: "${updateData.status.toLowerCase()}"`);
             } else {
               // Pour deliveries standard: statut en majuscules
               window.dispatchEvent(new CustomEvent('delivery-status-changed', { 
                 detail: { 
                   id: updateData.deliveryId, 
                   status: updateData.status.toUpperCase(),
                   originalStatus: updateData.status,
                   type: 'standard',
                   forceRefresh: true
                 } 
               }));
               console.log(`Événement de statut émis avec statut en majuscules: "${updateData.status.toUpperCase()}"`);
             }
          } catch (refreshError) {
            console.error(`Erreur lors du forçage du rafraîchissement:`, refreshError);
          }

          // Supprimer la mise à jour du stockage local
          await dataService.removePendingUpdate(updateData.id);

          return responseData;
        } else {
          // Si hors ligne, mettre en file d'attente pour synchronisation ultérieure
          console.log(`Mise en file d'attente de la mise à jour shop ${updateData.id} pour la livraison ${updateData.deliveryId} (mode hors ligne)`);
          
          // S'assurer que les images sont bien associées à cette mise à jour
          // (dans le cas où les IDs d'image sont déjà disponibles)
          if (serverImageIds.length > 0 || updateData.imageIds?.length > 0) {
            // Fusionner les IDs d'images serveur et locaux pour être sûr
            updateData.serverImageIds = [...(updateData.serverImageIds || []), ...serverImageIds];
            
            // Si on a des IDs d'images en attente, s'assurer qu'ils sont préservés
            if (!updateData.imageIds) {
              updateData.imageIds = [];
            }
            
            // Marquer les images comme faisant partie de cette mise à jour pour les synchroniser ensemble plus tard
            if (updateData.imageIds.length > 0) {
              console.log(`Association de ${updateData.imageIds.length} images avec la mise à jour shop ${updateData.id}`);
              
              // Pour chaque image en attente, s'assurer qu'elle est correctement configurée pour la synchro
              for (const imageId of updateData.imageIds) {
                try {
                  const imageData = await dataService.getPendingImage(imageId);
                  if (imageData) {
                    // Mettre à jour les données pour assurer une synchro correcte
                    imageData.deliveryId = updateData.deliveryId;
                    imageData.type = 'shop_delivery';
                    imageData.synced = false;
                    imageData.updateId = updateData.id; // Lier l'image à cette mise à jour
                    
                    if (!imageData.deliveryContext) {
                      imageData.deliveryContext = 'shop_photo';
                    }
                    
                    await dataService.updatePendingImage(imageId, imageData);
                    console.log(`Image ${imageId} associée à la mise à jour shop ${updateData.id}`);
                  }
                } catch (imgError) {
                  console.warn(`Erreur lors de la mise à jour de l'image ${imageId}:`, imgError);
                }
              }
            }
          }
          
          // Marquer cette mise à jour pour synchronisation future
          updateData.needsSync = true;
          updateData.queuedOffline = true;
          updateData.queuedAt = new Date().toISOString();
          
          // Mettre à jour l'état stocké
          await dataService.storePendingUpdate(updateData);
          
          // Log de l'action
          logService.syncInfo(`Mise à jour shop ${updateData.id} mise en file d'attente pour synchronisation ultérieure`, {
            deliveryId: updateData.deliveryId,
            status: updateData.status,
            images: updateData.imageIds || [],
            userEmail
          });
          
          // Émettre un événement pour mettre à jour l'UI
          this.emitDeliveryUpdatedEvent(updateData.deliveryId, updateData.status);
          
          return {
            success: true,
            synced: false,
            queuedOffline: true,
            message: "Mise à jour enregistrée pour synchronisation ultérieure"
          };
        }
      } finally {
        // Toujours libérer le verrou, quoi qu'il arrive
        localStorage.removeItem(lockKey);
        console.log(`Verrou libéré pour shop_delivery ${updateData.deliveryId}`);
      }
    } catch (error) {
      // Gestion d'erreur
      // En cas d'erreur, marquer la mise à jour pour une nouvelle tentative
      try {
        // Calculer le délai avant la prochaine tentative (backoff exponentiel limité)
        const attempts = (updateData.syncAttempts || 0) + 1;
        const backoffMinutes = Math.min(30, 5 * Math.pow(2, attempts - 1)); // 5, 10, 20, 30 min max
        const nextAttemptTime = new Date(Date.now() + backoffMinutes * 60 * 1000);
        
        // Mettre à jour les informations
        updateData.syncAttempts = attempts;
        updateData.lastSyncAttempt = new Date().toISOString();
        updateData.nextAttemptAfter = nextAttemptTime.toISOString();
        updateData.lastError = {
          message: error.message,
          status: error.response?.status,
          statusText: error.response?.statusText,
          time: new Date().toISOString(),
          type: error.name || 'UnknownError',
          isNetworkError: !navigator.onLine || error.message.includes('Network Error'),
          responseData: error.response?.data
        };
        
        // Enregistrer les informations de tentative
        await dataService.storePendingUpdate(updateData);
        
        // Planifier une nouvelle tentative automatique
        this.scheduleUpdateRetry(updateData.id, updateData);
      } catch (storageError) {
        logService.syncError(`Impossible de mettre à jour les informations de tentative pour ${updateData.id}`, {
          deliveryId: updateData.deliveryId,
          error: storageError.message,
          userEmail: localStorage.getItem('email') || 'email_non_disponible',
          originalError: error.message
        });
      }
      
      // Propager l'erreur
      throw error;
    }
  }

  // Méthode de synchronisation (version unique et consolidée)
  // Cette méthode a été fusionnée avec l'autre implémentation pour éviter les doublons

  // ... rest of the existing methods ...

  // Ajouter cette nouvelle méthode avant la fin de la classe SyncService
  /**
   * Nettoie les images qui ont été correctement synchronisées avec le serveur
   * @returns {Promise<{deleted: number, kept: number}>} Statistiques de nettoyage
   */
  async cleanupSyncedImages() {
    try {
      console.log("Démarrage du nettoyage des images synchronisées...");
      
      // Récupération de toutes les images en attente
      const pendingImages = await dataService.getPendingImages();
      console.log(`${pendingImages.length} images trouvées dans le stockage`);
      
      // Statistiques
      const stats = {
        total: pendingImages.length,
        deleted: 0,
        kept: 0,
        reasons: {
          synced: 0,
          duplicates: 0
        }
      };
      
      // Collecter les IDs d'images pour détecter les doublons
      const processedIds = new Set();
      const duplicates = [];
      
      // ÉTAPE 1: Supprimer les images déjà synchronisées et détecter les doublons
      for (const image of pendingImages) {
        if (!image || !image.id) continue;
        
        // Détection des doublons
        if (processedIds.has(image.id)) {
          duplicates.push(image.id);
          continue;
        }
        processedIds.add(image.id);
        
        // Si l'image est déjà synchronisée, la supprimer
        if (image.synced && image.serverImageId) {
          try {
            await dataService.removePendingImage(image.id);
            stats.deleted++;
            stats.reasons.synced++;
            console.log(`Image ${image.id} supprimée car déjà synchronisée (serverImageId: ${image.serverImageId})`);
          } catch (error) {
            console.error(`Erreur lors de la suppression de l'image synchronisée ${image.id}:`, error);
          }
        } else {
          stats.kept++;
        }
      }
      
      // ÉTAPE 2: Supprimer les doublons
      if (duplicates.length > 0) {
        console.log(`${duplicates.length} images dupliquées détectées, nettoyage...`);
        
        for (const duplicateId of duplicates) {
          try {
            // Garder la première occurrence, supprimer les autres
            const allInstances = pendingImages.filter(img => img && img.id === duplicateId);
            
            // Garder celle qui est synchronisée s'il y en a une
            const syncedInstance = allInstances.find(img => img.synced && img.serverImageId);
            
            if (syncedInstance) {
              // Supprimer toutes les autres instances
              for (const instance of allInstances) {
                if (instance !== syncedInstance) {
                  await dataService.removePendingImage(instance.id);
                  stats.deleted++;
                  stats.reasons.duplicates++;
                }
              }
            } else {
              // Garder la première, supprimer les autres
              const [kept, ...toDelete] = allInstances;
              for (const instance of toDelete) {
                await dataService.removePendingImage(instance.id);
                stats.deleted++;
                stats.reasons.duplicates++;
              }
            }
          } catch (error) {
            console.error(`Erreur lors du nettoyage des doublons pour ${duplicateId}:`, error);
          }
        }
      }
      
      // ÉTAPE 3: Utiliser aussi la méthode de purge standard
      const purgeResults = await this.purgeImageStorage(12); // Purger les images de plus de 12h
      
      // Combiner les statistiques
      stats.deleted += purgeResults.deleted;
      stats.kept = purgeResults.kept; // Mettre à jour le chiffre avec le résultat final
      
      // Log le résultat du nettoyage
      console.log("Nettoyage des images terminé:", {
        total: stats.total,
        deleted: stats.deleted,
        kept: stats.kept,
        reasons: stats.reasons,
        purgeResults
      });
      
      return {
        deleted: stats.deleted,
        kept: stats.kept,
        reasons: stats.reasons
      };
    } catch (error) {
      logService.syncError(`Erreur lors du nettoyage des images synchronisées`, {
        error: error.message,
        stack: error.stack
      });
      
      return { deleted: 0, kept: 0 };
    }
  }

  /**
   * Nettoyage complet du stockage d'images
   * Supprime les images synchronisées et celles qui sont trop anciennes
   * @param {number} maxAgeHours - Nombre d'heures maximum de conservation des images
   * @returns {Promise<{deleted: number, kept: number, reasons: Object}>} Statistiques de nettoyage
   */
  async purgeImageStorage(maxAgeHours = 48) {
    try {
      const pendingImages = await dataService.getPendingImages();
      
      const now = new Date();
      const stats = {
        total: pendingImages.length,
        deleted: 0,
        keptTotal: 0,
        reasons: {
          synced: 0,
          tooOld: 0,
          orphaned: 0,
          inUse: 0
        }
      };
      
      // Récupérer toutes les mises à jour en attente pour identifier les images encore utilisées
      const pendingUpdates = await dataService.getPendingUpdates();
      
      // Collecter tous les IDs d'images référencés dans les mises à jour
      const referencedImageIds = new Set();
      pendingUpdates.forEach(update => {
        if (update.imageIds && Array.isArray(update.imageIds)) {
          update.imageIds.forEach(id => referencedImageIds.add(id));
        }
        if (update.photo && typeof update.photo === 'string' && 
           (update.photo.startsWith('img_') || update.photo.startsWith('shop_img_'))) {
          referencedImageIds.add(update.photo);
        }
      });
      
      // Traiter chaque image
      for (const image of pendingImages) {
        if (!image || !image.id) continue;
        
        let shouldDelete = false;
        let deleteReason = '';
        
        // 1. Si image synchronisée, la supprimer
        if (image.synced && image.serverImageId) {
          shouldDelete = true;
          deleteReason = 'synced';
          stats.reasons.synced++;
        } 
        // 2. Si image trop ancienne (plus de maxAgeHours heures), la supprimer
        else {
          const timestamp = new Date(image.timestamp || image.createdAt || 0);
          const ageHours = Math.floor((now - timestamp) / (1000 * 60 * 60));
          
          if (ageHours > maxAgeHours) {
            shouldDelete = true;
            deleteReason = 'tooOld';
            stats.reasons.tooOld++;
          }
          // 3. Si image n'est référencée par aucune mise à jour, la supprimer
          else if (!referencedImageIds.has(image.id)) {
            shouldDelete = true;
            deleteReason = 'orphaned';
            stats.reasons.orphaned++;
          }
          // 4. Sinon, la garder
          else {
            stats.reasons.inUse++;
            stats.keptTotal++;
          }
        }
        
        // Supprimer l'image si nécessaire
        if (shouldDelete) {
          try {
            // Supprimer de IndexedDB
            await dataService.removePendingImage(image.id);
            
            // Supprimer également du cache des objets File
            if (this.imageObjectsStore) {
              await this.imageObjectsStore.removeItem(image.id);
            }
            
            stats.deleted++;
            
            logService.syncInfo(`Image ${image.id} supprimée du stockage (raison: ${deleteReason})`, {
              deliveryId: image.deliveryId,
              age: image.timestamp ? Math.floor((now - new Date(image.timestamp)) / (1000 * 60 * 60)) + 'h' : 'inconnu',
              synced: image.synced
            });
          } catch (error) {
            logService.syncWarning(`Échec de la suppression de l'image ${image.id}`, {
              error: error.message,
              reason: deleteReason
            });
          }
        }
      }
      
      // Log du résultat
      logService.syncInfo(`Nettoyage complet des images terminé`, {
        stats: stats,
        timestamp: now.toISOString()
      });
      
      return {
        deleted: stats.deleted,
        kept: stats.keptTotal,
        reasons: stats.reasons
      };
    } catch (error) {
      logService.syncError(`Erreur lors du nettoyage du stockage d'images`, {
        error: error.message,
        stack: error.stack
      });
      
      return { deleted: 0, kept: 0, reasons: {} };
    }
  }
}

export const syncService = new SyncService();
export default syncService;
